import { useState, useCallback, useEffect, useRef, type ReactNode, type ComponentType } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams, useLocation } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Clapperboard, Video, Users, Package, Clock, BookOpen,
  Trash2, Sparkles, Lock, Pencil, Save, Download,
  Film, UserCircle, Target, Layers, Check, X, Loader2, ImageIcon,
  RefreshCw, ChevronDown, ChevronUp, Lightbulb, Zap, Heart, MapPin, Star,
  Mic, AlertTriangle, Captions, Wand2, Upload, Pause, FileText, ListOrdered,
  HelpCircle, ArrowRightLeft, ShieldCheck, Drama,
} from "lucide-react";
import type { ClaymationCampaign, ClaymationAvatar, ClaymationScript, ClaymationScriptPlan, Persona, Product, NarrativeAngle, ClaymationRender } from "@shared/schema";
import { ScriptRenderControls } from "@/components/script-render-controls";
import { ScenePanelReview } from "@/components/scene-panel-review";
import { StoryboardPanelGrid } from "@/components/storyboard-panel-grid";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { optimizedImg } from "@/lib/img";

// Mirror of the backend `allSceneClipsReady` (server/routes.ts): a script's
// final video can only be stitched once EVERY canonical scene (extracted panel,
// or prompted scene for legacy scripts) has a rendered clip. Keep this in
// lockstep with the backend gate to avoid producing incomplete stitched videos.
function allClipsReady(s: any): boolean {
  const vs = (s?.sceneVideos as any[]) || [];
  if (vs.length === 0) return false;
  if (!vs.every((v: any) => v?.videoUrl)) return false;
  // Canonical scene set = extracted panels (one clip per frame); fall back to
  // prompted scenes for legacy scripts. Mirrors backend allSceneClipsReady.
  const panels = (s?.panelImages as any[]) || [];
  const canonical = (panels.length > 0
    ? panels
    : ((s?.scenePrompts as any[]) || []).filter((p: any) => p?.prompt)
  ).map((x: any) => Number(x.scene_number));
  if (canonical.length === 0) return false;
  return canonical.every((n: number) => vs.some((v: any) => Number(v.scene_number) === n && v?.videoUrl));
}

// Bring-your-own storyboard: upload a storyboard grid image for a single script
// and let the server slice it + run the rest of the pipeline (panel extraction).
// On success the central poll picks up panelStatus="generating" and the panel
// grid fills in automatically.
function StoryboardUpload({ script, campaignId }: { script: any; campaignId: string }) {
  const { toast } = useToast();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const hasStoryboard = !!script?.storyboardImageUrl;

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
      toast({ title: "Unsupported file", description: "Upload a PNG, JPG, or WebP image.", variant: "destructive" });
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("slot", "1");
      const res = await fetch(`/api/claymation-scripts/${script.id}/upload-storyboard`, {
        method: "POST",
        body: fd,
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: "Upload failed" }));
        throw new Error(err.message || "Upload failed");
      }
      toast({ title: "Storyboard uploaded", description: "Extracting panels…" });
      queryClient.invalidateQueries({ queryKey: ["/api/claymation-campaigns", campaignId, "scripts"] });
    } catch (e: any) {
      toast({ title: "Upload failed", description: e?.message || String(e), variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="mt-2">
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={onFile}
        data-testid={`input-upload-storyboard-${script.id}`}
      />
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="w-full h-8 text-[11px] rounded-full border-dashed"
        disabled={uploading}
        onClick={() => inputRef.current?.click()}
        data-testid={`button-upload-storyboard-${script.id}`}
      >
        {uploading
          ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Uploading…</>
          : <><Upload className="h-3 w-3 mr-1" /> {hasStoryboard ? "Replace with your own storyboard" : "Upload your own storyboard"}</>}
      </Button>
      <p className="text-[9px] text-muted-foreground/60 mt-1 text-center">
        Drops straight into the pipeline — panels extract automatically. Use a 3×3 grid (9 scenes).
      </p>
    </div>
  );
}

function AssetDrawer({
  title,
  icon: Icon,
  iconClass,
  status,
  defaultOpen = false,
  testId,
  children,
}: {
  title: string;
  icon: ComponentType<{ className?: string }>;
  iconClass: string;
  status?: ReactNode;
  defaultOpen?: boolean;
  testId?: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <details
      open={open}
      className="group/drawer rounded-lg border border-border/30 bg-muted/[0.03] overflow-hidden"
      data-testid={testId}
    >
      <summary
        onClick={(e) => { e.preventDefault(); setOpen((o) => !o); }}
        className="flex items-center justify-between gap-2 px-3 py-2 cursor-pointer list-none select-none hover:bg-muted/10 transition-colors [&::-webkit-details-marker]:hidden"
      >
        <span className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-foreground/80">
          <Icon className={`h-3.5 w-3.5 ${iconClass}`} />
          {title}
        </span>
        <span className="flex items-center gap-2">
          {status}
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground/50 transition-transform group-open/drawer:rotate-180" />
        </span>
      </summary>
      <div className="px-3 pb-3 pt-0.5">{children}</div>
    </details>
  );
}

const statusColors: Record<string, string> = {
  draft: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  active: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300",
  scripting: "bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-300",
  producing: "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300",
  completed: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300",
};


// How long to keep refreshing the scripts list after scene-prompt generation
// is kicked off (it runs in the background server-side with no per-script
// "generating" status, so the client must poll until prompts actually land).
const SCENE_PROMPTS_MAX_MS = 13 * 60 * 1000;
const scenePromptsPendingKey = (campaignId: string) => `claymation-scene-prompts-pending:${campaignId}`;

// The Concept Planner returns a single concept named `concept_name` plus a set
// of strategic planning fields. These are the human-readable ones we surface on
// the concept card (the rest — model/tokens/raw content — are noise).
const CLAYMATION_CONCEPT_FIELDS: { key: string; label: string; icon: ComponentType<{ className?: string }> }[] = [
  { key: "native_content_premise", label: "Premise", icon: BookOpen },
  { key: "opening_hook", label: "Opening hook", icon: Sparkles },
  { key: "core_tension", label: "Core tension", icon: AlertTriangle },
  { key: "viewer_question", label: "Viewer question", icon: HelpCircle },
  { key: "belief_shift", label: "Belief shift", icon: ArrowRightLeft },
  { key: "product_role", label: "Product role", icon: Package },
  { key: "reason_to_believe", label: "Reason to believe", icon: ShieldCheck },
  { key: "visual_engine", label: "Visual engine", icon: Film },
];

// Coerce a concept field to display text. Handles strings, numbers, and the
// belief_shift `{ from, to }` object shape some Sim responses return.
function conceptText(v: any): string {
  if (v == null) return "";
  if (typeof v === "string") return v.trim();
  if (typeof v === "number") return String(v);
  if (typeof v === "object" && !Array.isArray(v)) {
    const from = typeof v.from === "string" ? v.from.trim() : "";
    const to = typeof v.to === "string" ? v.to.trim() : "";
    if (from || to) return `From "${from}" to "${to}"`;
  }
  return "";
}

const humanizeKey = (k: string): string =>
  k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Renders the FULL claymation concept (all rich planning fields) from a saved
// plan's conceptJson. Falls back to the plain conceptDescription text when the
// stored concept has no structured fields (older/blank responses).
function ConceptDetails({ concept, fallback }: { concept: unknown; fallback?: string | null }) {
  const [open, setOpen] = useState(false);
  const c =
    concept && typeof concept === "object" && !Array.isArray(concept)
      ? (concept as Record<string, any>)
      : null;

  // The premise is the headline of the concept — surfaced as the lead paragraph,
  // never tucked into the collapsible breakdown.
  const premise = c ? conceptText(c.native_content_premise) : "";
  const insightFields = c
    ? CLAYMATION_CONCEPT_FIELDS.filter((f) => f.key !== "native_content_premise")
        .map((f) => ({ label: f.label, icon: f.icon, value: conceptText(c[f.key]) }))
        .filter((f) => f.value)
    : [];

  // Storytelling spine: { beginning, middle, end } (ordered) or a plain string.
  const spine: { label: string; value: string }[] = [];
  const spineRaw = c?.storytelling_spine;
  if (spineRaw && typeof spineRaw === "object" && !Array.isArray(spineRaw)) {
    const order = ["beginning", "middle", "end"];
    const rank = (k: string) => (order.indexOf(k) === -1 ? 99 : order.indexOf(k));
    for (const k of Object.keys(spineRaw).sort((a, b) => rank(a) - rank(b))) {
      const v = conceptText((spineRaw as any)[k]);
      if (v) spine.push({ label: humanizeKey(k), value: v });
    }
  } else if (typeof spineRaw === "string" && spineRaw.trim()) {
    spine.push({ label: "Story spine", value: spineRaw.trim() });
  }

  const storyArc = conceptText(c?.story_arc);
  const lengthSeconds = conceptText(c?.length_seconds);
  const sceneCount = conceptText(c?.scene_count);
  const storyboardCount = conceptText(c?.storyboard_count);
  const metaChips: { icon: ComponentType<{ className?: string }>; label: string }[] = [];
  if (storyArc) metaChips.push({ icon: Drama, label: storyArc });
  if (lengthSeconds) metaChips.push({ icon: Clock, label: `${lengthSeconds}s` });
  if (sceneCount) metaChips.push({ icon: Clapperboard, label: `${sceneCount} scenes` });
  if (storyboardCount)
    metaChips.push({ icon: Layers, label: `${storyboardCount} storyboard${storyboardCount === "1" ? "" : "s"}` });

  const hasRich = !!premise || insightFields.length > 0 || spine.length > 0 || metaChips.length > 0;
  if (!hasRich) {
    const plain =
      (fallback && fallback.trim()) ||
      conceptText(c?.concept_summary) ||
      conceptText(c?.concept_description);
    return plain ? (
      <p className="text-sm text-muted-foreground whitespace-pre-line">{plain}</p>
    ) : null;
  }

  const hasMore = insightFields.length > 0 || spine.length > 0;

  return (
    <div className="space-y-3">
      {metaChips.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {metaChips.map((chip, i) => {
            const Icon = chip.icon;
            return (
              <span
                key={`${i}-${chip.label}`}
                className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-400 px-2 py-0.5 text-[11px] font-medium"
              >
                <Icon className="h-3 w-3" />
                {chip.label}
              </span>
            );
          })}
        </div>
      )}

      {premise && (
        <p className="text-sm leading-relaxed text-foreground/90 whitespace-pre-line">{premise}</p>
      )}

      {hasMore && (
        <div className="space-y-3">
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-amber-600 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300 transition-colors"
            data-testid="button-toggle-concept-details"
          >
            {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            {open ? "Hide breakdown" : "Story breakdown"}
          </button>

          {open && (
            <div className="space-y-4">
              {insightFields.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {insightFields.map((f) => {
                    const Icon = f.icon;
                    return (
                      <div
                        key={f.label}
                        className="rounded-lg border border-border/40 bg-muted/30 dark:bg-muted/15 p-3 space-y-1"
                      >
                        <div className="flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground/80">
                          <Icon className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                          {f.label}
                        </div>
                        <p className="text-[13px] leading-snug text-foreground/90 whitespace-pre-line">{f.value}</p>
                      </div>
                    );
                  })}
                </div>
              )}

              {spine.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground/80">
                    <ListOrdered className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                    Story spine
                  </div>
                  <div className="space-y-2.5">
                    {spine.map((s, i) => (
                      <div key={s.label} className="flex gap-2.5">
                        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400 text-[10px] font-bold">
                          {i + 1}
                        </span>
                        <div className="space-y-0.5 min-w-0">
                          <p className="text-xs font-semibold text-foreground/80">{s.label}</p>
                          <p className="text-[13px] leading-snug text-muted-foreground whitespace-pre-line">{s.value}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// The beat map is generated in the background AFTER a script's voice-over
// finishes (its voiceOverStatus flips to "ready" first). There is no
// "beats generating" status — only "ready"/"failed" are persisted onto
// fullScript once the beat map settles. So a script is mid-beat-map whenever
// the voice-over is ready but the beats array hasn't landed yet and hasn't
// been marked failed. Used both to keep the scripts query polling and to count
// the Script step as in-flight through that window — without it, polling stops
// the instant the voice-over is ready and the finished beat map is never seen.
function parseScriptFull(s: any): any {
  try {
    return typeof s?.fullScript === "string" ? JSON.parse(s.fullScript) : (s?.fullScript || {});
  } catch {
    return {};
  }
}
// The beat map is its own production step that runs AFTER the Script step, so it
// only runs when explicitly triggered. In-flight is now an explicit persisted
// marker (beatsStatus === "generating") set by the beat-map endpoint — NOT
// inferred from "voice ready + no beats" (which would be true for every voiced
// script before its beat map is ever requested).
function beatMapInProgress(s: any): boolean {
  return parseScriptFull(s)?.beatsStatus === "generating";
}

export default function ClaymationCampaignDetailPage() {
  const { brandId, claymationCampaignId } = useParams<{ brandId: string; claymationCampaignId: string }>();
  const [, navigate] = useLocation();
  const { toast } = useToast();


  const [lightboxAvatar, setLightboxAvatar] = useState<ClaymationAvatar | null>(null);
  const [expandedScriptId, setExpandedScriptId] = useState<string | null>(null);
  const [expandedPlanId, setExpandedPlanId] = useState<string | null>(null);
  const [editingScriptId, setEditingScriptId] = useState<string | null>(null);
  const [editingScenes, setEditingScenes] = useState<any[] | null>(null);
  const [editingTitle, setEditingTitle] = useState<string>("");

  const { data: campaign, isLoading: campaignLoading } = useQuery<ClaymationCampaign>({
    queryKey: ["/api/claymation-campaigns", claymationCampaignId],
    queryFn: async () => {
      const res = await fetch(`/api/claymation-campaigns/${claymationCampaignId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch campaign");
      return res.json();
    },
    enabled: !!claymationCampaignId,
  });

  const { data: personas } = useQuery<Persona[]>({
    queryKey: [`/api/brands/${brandId}/personas`],
    enabled: !!brandId,
  });
  const { data: products } = useQuery<Product[]>({
    queryKey: [`/api/brands/${brandId}/products`],
    enabled: !!brandId,
  });
  const { data: narrativeAnglesData } = useQuery<NarrativeAngle[]>({
    queryKey: [`/api/brands/${brandId}/narrative-angles`],
    enabled: !!brandId,
  });

  const { data: personaCharacters } = useQuery<any[]>({
    queryKey: [`/api/personas/${campaign?.personaId}/characters`],
    enabled: !!campaign?.personaId,
  });

  const { data: avatars, isLoading: avatarsLoading } = useQuery<ClaymationAvatar[]>({
    queryKey: ["/api/claymation-campaigns", claymationCampaignId, "avatars"],
    queryFn: async () => {
      const res = await fetch(`/api/claymation-campaigns/${claymationCampaignId}/avatars`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch avatars");
      return res.json();
    },
    enabled: !!claymationCampaignId,
    // Poll while any avatar is still rendering (status "pending");
    // the generate-avatars route is now non-blocking (202 + background).
    refetchInterval: (q) =>
      (q.state.data as any[] | undefined)?.some((a) => a?.status === "pending") ? 4000 : false,
  });

  type PreviousAvatarGroup = { campaignId: string; campaignName: string; avatars: ClaymationAvatar[] };
  const { data: previousAvatarGroups } = useQuery<PreviousAvatarGroup[]>({
    queryKey: ["/api/claymation-campaigns", claymationCampaignId, "previous-avatars"],
    queryFn: async () => {
      const res = await fetch(`/api/claymation-campaigns/${claymationCampaignId}/previous-avatars`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch previous avatars");
      return res.json();
    },
    enabled: !!claymationCampaignId,
  });

  const hasPreviousAvatars = previousAvatarGroups && previousAvatarGroups.length > 0;

  const [isGeneratingPrompts, setIsGeneratingPrompts] = useState(false);
  const [isGeneratingVideos, setIsGeneratingVideos] = useState(false);
  // "Generate All" auto-pilot: walks the pipeline steps in order, firing each
  // step's action once the previous one finishes. The per-render step snapshot is
  // stashed in pipelineRef (computed where the steps array lives) so a top-level
  // effect can advance without duplicating the gating logic.
  const [autoRunPipeline, setAutoRunPipeline] = useState(false);
  const pipelineRef = useRef<{
    key: string; done: boolean; skip: boolean; busy: boolean; disabled: boolean; gated: boolean; failed: boolean; onClick: () => void;
  }[] | null>(null);
  const autoRunFiredRef = useRef<string | null>(null);
  // Declared here (above the scripts query) because the query's refetchInterval
  // references it and is evaluated during the useQuery() call.
  const [isGeneratingScenePrompts, setIsGeneratingScenePrompts] = useState(false);
  const [isExtractingPanels, setIsExtractingPanels] = useState(false);
  // True while the script-first generate-scripts request is writing narration,
  // so the scripts list polls and freshly-written narration appears live.
  const [isWritingScripts, setIsWritingScripts] = useState(false);
  // Beat Map is its own step (after Script). This keeps the scripts list polling
  // while the (synchronous) beat-map endpoint runs so beats surface even if the
  // held request is dropped (the persisted beatsStatus="generating" is the
  // durable signal; this is the in-browser companion).
  const [isGeneratingBeats, setIsGeneratingBeats] = useState(false);
  // Concept Generator (concept-first, UGC parity): declared here (above the
  // plans query) because the query's refetchInterval reads them during the
  // useQuery() call. `isGeneratingConcepts` keeps the plans list polling while
  // the (synchronous) planner runs so concepts surface even if the held request
  // is dropped; `conceptFailed` drives the auto-pilot "failed" stop + retry UI.
  const [isGeneratingConcepts, setIsGeneratingConcepts] = useState(false);
  const [conceptFailed, setConceptFailed] = useState(false);
  // Synchronous in-flight guard for concept generation. The auto-setup effect
  // and the "Generate All" auto-pilot can both target the concept step in the
  // same render tick, and the isPending / isGeneratingConcepts flags only flip
  // on the NEXT render — too late to dedupe. This ref flips synchronously so a
  // duplicate trigger (same tick, or while the long synchronous
  // generate-script-plans request is still held open) is dropped instead of
  // launching a second concept generation. Reset when the mutation settles.
  const conceptGenInFlightRef = useRef(false);
  // Inline per-concept editing state for the concept review cards.
  const [editingPlanId, setEditingPlanId] = useState<string | null>(null);
  const [editingPlanTitle, setEditingPlanTitle] = useState("");
  const [editingPlanDescription, setEditingPlanDescription] = useState("");

  const { data: scripts, isLoading: scriptsLoading } = useQuery<ClaymationScript[]>({
    queryKey: ["/api/claymation-campaigns", claymationCampaignId, "scripts"],
    queryFn: async () => {
      const res = await fetch(`/api/claymation-campaigns/${claymationCampaignId}/scripts`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch scripts");
      return res.json();
    },
    enabled: !!claymationCampaignId,
    refetchInterval: (q) => {
      if (isWritingScripts) return 4000;
      if (isGeneratingBeats) return 3000;
      if (isGeneratingPrompts || isGeneratingScenePrompts || isGeneratingVideos) return 5000;
      const data = (q.state.data as ClaymationScript[] | undefined) || [];
      if (data.some((s: any) => s.storyboardStatus === "generating" || s.storyboardStatus2 === "generating" || s.storyboardStatus3 === "generating")) return 3000;
      if (data.some((s: any) => s.voiceOverStatus === "generating")) return 3000;
      // The Beat Map step persists an explicit beatsStatus="generating" marker
      // while it runs; keep polling through that window until the beats land (or
      // fail) so the step updates live even if the held request was dropped.
      if (data.some(beatMapInProgress)) return 3000;
      if (data.some((s: any) => s.panelStatus === "generating")) return 3000;
      // Keep polling while any single panel edit is still pending, so the
      // regenerated panel swaps in live (the spinner clears when image_url
      // changes or edit_status flips to error).
      if (data.some((s: any) =>
        Array.isArray(s.panelImages) &&
        (s.panelImages as any[]).some((p: any) => p?.edit_status === "pending")
      )) return 3000;
      // Keep polling while any panel upscale is queued/processing, so the
      // 4K-upscaled image_url swaps in live as each finishes. The upscale flow
      // tracks progress on upscale_status (NOT edit_status), so without this the
      // grid freezes on the pre-upscale images once the single onSuccess
      // refetch lands.
      if (data.some((s: any) =>
        Array.isArray(s.panelImages) &&
        (s.panelImages as any[]).some((p: any) => p?.upscale_status === "queued" || p?.upscale_status === "processing")
      )) return 3000;
      // Keep polling if any scene-video is still rendering in the background
      // (so the UI updates even if the user navigated away and came back).
      const anyPendingVideo = data.some((s: any) => {
        const vids = s.sceneVideos as any[] | null;
        return Array.isArray(vids) && vids.some((v: any) => v && !v.videoUrl && !v.error && v.status !== "paused");
      });
      if (anyPendingVideo) return 5000;
      return false;
    },
  });

  // Concept plans (concept-first, UGC parity). The Concept Generator runs
  // before scripts; once locked, generate-scripts builds one container per plan.
  // Polls while the planner is running so concepts surface even if the held
  // synchronous request is dropped by a gateway timeout.
  const { data: plans, isLoading: plansLoading } = useQuery<ClaymationScriptPlan[]>({
    queryKey: ["/api/claymation-campaigns", claymationCampaignId, "script-plans"],
    queryFn: async () => {
      const res = await fetch(`/api/claymation-campaigns/${claymationCampaignId}/script-plans`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch concept plans");
      return res.json();
    },
    enabled: !!claymationCampaignId,
    refetchInterval: () => (isGeneratingConcepts ? 4000 : false),
  });

  // Latest final-stitch render per script for this campaign. Polled while
  // any render is queued/rendering so the inline status badges and
  // production-ready button update in near-real-time.
  const { data: rendersByScript } = useQuery<Record<string, ClaymationRender>>({
    queryKey: ["/api/claymation-campaigns", claymationCampaignId, "renders", "latest"],
    queryFn: async () => {
      const res = await fetch(`/api/claymation-campaigns/${claymationCampaignId}/renders/latest`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch renders");
      return res.json();
    },
    enabled: !!claymationCampaignId,
    refetchInterval: (q) => {
      const data = q.state.data as Record<string, any> | undefined;
      if (!data) return false;
      const anyActive = Object.values(data).some((r) =>
        r?.status === "queued" || r?.status === "rendering" || r?.captionedVideoStatus === "generating"
      );
      return anyActive ? 2500 : false;
    },
  });

  const renderAllMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/claymation-campaigns/${claymationCampaignId}/render-all`);
      return res.json();
    },
    onSuccess: (data: any) => {
      const queued = (data.renders || []).filter((r: any) => r.render && !r.skippedReason).length;
      const skipped = (data.renders || []).filter((r: any) => r.skippedReason).length;
      toast({
        title: `Stitching ${queued} final video${queued === 1 ? "" : "s"}`,
        description: skipped > 0 ? `${skipped} skipped (already running or no scenes)` : "Polling for progress…",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/claymation-campaigns", claymationCampaignId, "renders", "latest"] });
    },
    onError: (e: any) => {
      toast({ title: "Failed to start renders", description: e.message, variant: "destructive" });
    },
  });

  // Fan-out caption (re)generation across every completed render in the
  // campaign. Per-render endpoint returns 202 + atomic 409 if already
  // running, so it's safe to call broadly. We invalidate once after the
  // batch settles and let the poll surface live status.
  const captionAllMutation = useMutation({
    mutationFn: async (renderIds: string[]) => {
      const results = await Promise.allSettled(
        renderIds.map((id) =>
          fetch(`/api/claymation-renders/${id}/generate-captions`, { method: "POST", credentials: "include" })
            .then((r) => ({ ok: r.ok || r.status === 202 || r.status === 409, status: r.status }))
        )
      );
      const queued = results.filter((r) => r.status === "fulfilled" && (r as any).value.ok && (r as any).value.status !== 409).length;
      const already = results.filter((r) => r.status === "fulfilled" && (r as any).value.status === 409).length;
      const failed = results.length - queued - already;
      return { queued, already, failed };
    },
    onSuccess: ({ queued, already, failed }) => {
      toast({
        title: `Captioning ${queued} video${queued === 1 ? "" : "s"}`,
        description: [
          already > 0 ? `${already} already running` : null,
          failed > 0 ? `${failed} failed to start` : null,
        ].filter(Boolean).join(" · ") || "Polling for progress…",
        variant: failed > 0 ? "destructive" : "default",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/claymation-campaigns", claymationCampaignId, "renders", "latest"] });
    },
    onError: (e: any) => {
      toast({ title: "Failed to start captioning", description: e.message, variant: "destructive" });
    },
  });

  const generateScriptsMutation = useMutation({
    mutationFn: async (opts?: { replace?: boolean }) => {
      const res = await apiRequest("POST", `/api/claymation-campaigns/${claymationCampaignId}/generate-scripts`, opts?.replace ? { replace: true } : undefined);
      return res.json();
    },
    onMutate: () => setIsWritingScripts(true),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/claymation-campaigns", claymationCampaignId, "scripts"] });
      if (data?.alreadyRunning) return;
      toast({ title: `${data.successCount} scripts ready`, description: data.failCount > 0 ? `${data.failCount} failed` : undefined });
    },
    onError: (e: any) => {
      toast({ title: "Failed to write scripts", description: e.message, variant: "destructive" });
    },
    onSettled: () => {
      setIsWritingScripts(false);
      queryClient.invalidateQueries({ queryKey: ["/api/claymation-campaigns", claymationCampaignId, "scripts"] });
    },
  });

  // Beat Map step (after Script). Transcribes each script's voice-over and runs
  // the claymation Beat Map Generator; idempotent server-side (only voiced
  // scripts missing beats, unless `replace` regenerates them all).
  const generateBeatMapsMutation = useMutation({
    mutationFn: async (opts?: { replace?: boolean }) => {
      const res = await apiRequest("POST", `/api/claymation-campaigns/${claymationCampaignId}/generate-beat-maps`, opts?.replace ? { replace: true } : undefined);
      return res.json();
    },
    onMutate: () => setIsGeneratingBeats(true),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/claymation-campaigns", claymationCampaignId, "scripts"] });
      if (data?.alreadyRunning) return;
      toast({ title: `${data.successCount} beat map${data.successCount === 1 ? "" : "s"} ready`, description: data.failCount > 0 ? `${data.failCount} failed` : undefined });
    },
    onError: (e: any) => {
      toast({ title: "Failed to generate beat maps", description: e.message, variant: "destructive" });
    },
    onSettled: () => {
      setIsGeneratingBeats(false);
      queryClient.invalidateQueries({ queryKey: ["/api/claymation-campaigns", claymationCampaignId, "scripts"] });
    },
  });

  // Concept Generator (runs before scripts). generate-script-plans is synchronous
  // (parallel ~per-concept Sim calls); isGeneratingConcepts keeps the plans list
  // polling so concepts surface even if the held request is dropped.
  const generateScriptPlansMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/claymation-campaigns/${claymationCampaignId}/generate-script-plans`);
      return res.json();
    },
    onMutate: () => {
      setConceptFailed(false);
      setIsGeneratingConcepts(true);
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/claymation-campaigns", claymationCampaignId, "script-plans"] });
      toast({ title: `${data?.count ?? 0} concept${(data?.count ?? 0) === 1 ? "" : "s"} ready` });
    },
    onError: (e: any) => {
      setConceptFailed(true);
      toast({ title: "Failed to generate concepts", description: e.message, variant: "destructive" });
    },
    onSettled: () => {
      conceptGenInFlightRef.current = false;
      setIsGeneratingConcepts(false);
      queryClient.invalidateQueries({ queryKey: ["/api/claymation-campaigns", claymationCampaignId, "script-plans"] });
    },
  });

  const regenerateScriptPlanMutation = useMutation({
    mutationFn: async (planId: string) => {
      const res = await apiRequest("POST", `/api/claymation-campaigns/${claymationCampaignId}/regenerate-script-plan/${planId}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/claymation-campaigns", claymationCampaignId, "script-plans"] });
    },
    onError: (e: any) => {
      toast({ title: "Failed to regenerate concept", description: e.message, variant: "destructive" });
    },
  });

  const updateScriptPlanMutation = useMutation({
    mutationFn: async ({ planId, ...data }: { planId: string; title?: string; conceptDescription?: string; avatarId?: string | null }) => {
      const res = await apiRequest("PATCH", `/api/claymation-script-plans/${planId}`, data);
      return res.json();
    },
    onSuccess: () => {
      setEditingPlanId(null);
      queryClient.invalidateQueries({ queryKey: ["/api/claymation-campaigns", claymationCampaignId, "script-plans"] });
    },
    onError: (e: any) => {
      toast({ title: "Failed to update concept", description: e.message, variant: "destructive" });
    },
  });

  const generateAvatarsMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/claymation-campaigns/${claymationCampaignId}/generate-avatars`);
      return res.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/claymation-campaigns", claymationCampaignId, "avatars"] });
      toast({ title: `Generated ${data.count} avatar images` });
    },
    onError: (e: any) => {
      toast({ title: "Failed to generate avatars", description: e.message, variant: "destructive" });
    },
  });

  const updateAvatarMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const res = await apiRequest("PATCH", `/api/claymation-avatars/${id}`, { status, claymationCampaignId });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/claymation-campaigns", claymationCampaignId, "avatars"] });
    },
    onError: (e: any) => {
      toast({ title: "Failed to update avatar", description: e.message, variant: "destructive" });
    },
  });

  const deleteAvatarMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/claymation-avatars/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/claymation-campaigns", claymationCampaignId, "avatars"] });
      toast({ title: "Avatar removed" });
    },
    onError: (e: any) => {
      toast({ title: "Failed to delete avatar", description: e.message, variant: "destructive" });
    },
  });

  const finalizeAvatarsMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/claymation-campaigns/${claymationCampaignId}/finalize-avatars`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/claymation-campaigns", claymationCampaignId, "avatars"] });
      toast({ title: "Avatars locked in", description: "Non-selected avatars have been removed." });
    },
    onError: (e: any) => {
      toast({ title: "Failed to finalize", description: e.message, variant: "destructive" });
    },
  });

  const importAvatarMutation = useMutation({
    mutationFn: async (avatar: ClaymationAvatar) => {
      const res = await apiRequest("POST", `/api/claymation-campaigns/${claymationCampaignId}/import-avatar`, {
        imageUrl: avatar.imageUrl,
        resolvedPrompt: avatar.resolvedPrompt,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/claymation-campaigns", claymationCampaignId, "avatars"] });
      toast({ title: "Avatar imported", description: "Previously used avatar added to this campaign." });
    },
    onError: (e: any) => {
      toast({ title: "Failed to import avatar", description: e.message, variant: "destructive" });
    },
  });

  const [avatarTab, setAvatarTab] = useState<"current" | "previous">("current");

  const updateScriptMutation = useMutation({
    mutationFn: async ({ scriptId, data }: { scriptId: string; data: any }) => {
      const res = await apiRequest("PATCH", `/api/claymation-scripts/${scriptId}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/claymation-campaigns", claymationCampaignId, "scripts"] });
      setEditingScriptId(null);
      setEditingScenes(null);
      toast({ title: "Script updated" });
    },
    onError: (e: any) => {
      toast({ title: "Failed to save script", description: e.message, variant: "destructive" });
    },
  });

  const [genProgress, setGenProgress] = useState<Record<string, "pending" | "generating" | "done" | "error">>({});
  // Whether, at the moment scene-prompt generation kicked off, at least one
  // ready script was still missing prompts. Used to safely auto-complete a
  // first-time generation early without prematurely stopping a regeneration
  // (where prior prompts are still present until new ones overwrite them).
  const scenePromptsBaselineIncompleteRef = useRef(false);
  // In-memory start time for the current scene-prompt generation. Source of
  // truth for the polling time window so it works even if localStorage is
  // unavailable (private mode / quota); localStorage only adds reload survival.
  const scenePromptsStartedAtRef = useRef(0);
  // True once we've observed at least one target script enter the server-side
  // "pending" state for the current run. Completion is then detected when no
  // target remains pending (server resolved every script to ready/error/idle),
  // which is deterministic — unlike the old 13-min timer that spun on failures.
  const scenePromptSawPendingRef = useRef(false);

  const [promptGenStatus, setPromptGenStatus] = useState<{ total: number; sent: number; done: number; errors: number }>({ total: 0, sent: 0, done: 0, errors: 0 });

  const generateAllPrompts = useCallback(async () => {
    if (!scripts || isGeneratingPrompts) return;
    const lockedScripts = scripts.filter(s => s.status === "locked" && s.avatarId);
    if (lockedScripts.length === 0) return;

    setIsGeneratingPrompts(true);
    setPromptGenStatus({ total: lockedScripts.length, sent: lockedScripts.length, done: 0, errors: 0 });

    try {
      const res = await fetch(`/api/claymation-campaigns/${claymationCampaignId}/generate-storyboards`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: "Failed to start storyboards" }));
        throw new Error(err.message || "Failed to start storyboards");
      }
      toast({ title: "Storyboard generation started", description: `${lockedScripts.length} script${lockedScripts.length === 1 ? "" : "s"} queued. This page will update as each finishes.` });
      queryClient.invalidateQueries({ queryKey: ["/api/claymation-campaigns", claymationCampaignId, "scripts"] });
    } catch (e: any) {
      toast({ title: "Storyboard generation failed", description: e?.message || String(e), variant: "destructive" });
    } finally {
      setIsGeneratingPrompts(false);
    }
  }, [scripts, claymationCampaignId, isGeneratingPrompts, toast]);

  // Per-storyboard (slot 1-3) regeneration. Re-runs only the chosen grid for one
  // script, leaving the other storyboards untouched. `regenSlots` gives the
  // button optimistic feedback for the brief window before polling observes the
  // server flip the slot to "generating".
  const [regenSlots, setRegenSlots] = useState<Set<string>>(new Set());
  const regenerateStoryboardSlot = useCallback(async (scriptId: string, slotIdx: number) => {
    const key = `${scriptId}:${slotIdx}`;
    if (regenSlots.has(key)) return;
    setRegenSlots((prev) => new Set(prev).add(key));
    try {
      await apiRequest("POST", `/api/claymation-scripts/${scriptId}/storyboards/${slotIdx}/regenerate`);
      toast({ title: "Regenerating storyboard", description: `Storyboard ${slotIdx} is regenerating. It will update here when it finishes.` });
      queryClient.invalidateQueries({ queryKey: ["/api/claymation-campaigns", claymationCampaignId, "scripts"] });
    } catch (e: any) {
      toast({ title: "Regeneration failed", description: e?.message || String(e), variant: "destructive" });
    } finally {
      setRegenSlots((prev) => { const n = new Set(prev); n.delete(key); return n; });
    }
  }, [regenSlots, claymationCampaignId, toast]);

  const generateScenePromptsFromStoryboard = useCallback(async () => {
    if (!scripts || isGeneratingScenePrompts) return;
    const ready = scripts.filter((s: any) => s.status === "locked" && s.avatarId && s.storyboardImageUrl && s.storyboardStatus === "ready");
    if (ready.length === 0) {
      toast({ title: "No storyboards ready", description: "Generate storyboards first.", variant: "destructive" });
      return;
    }
    // Record whether any ready script is still missing prompts so completion
    // can be detected early without prematurely stopping a regeneration.
    scenePromptsBaselineIncompleteRef.current = ready.some((s: any) => {
      const ps = (s.scenePrompts as any[]) || [];
      return !(ps.length > 0 && ps.every((p: any) => p?.prompt));
    });
    // Persist the pending marker BEFORE flipping the flag (and before awaiting)
    // so it is already written when the completion effect first runs — otherwise
    // the effect would read a missing marker, treat it as expired, and stop
    // polling immediately. Scene-prompt generation runs server-side with no
    // per-script status, so the central refetchInterval polls while
    // isGeneratingScenePrompts is set, and the resilience effects resume/clear it.
    scenePromptsStartedAtRef.current = Date.now();
    scenePromptSawPendingRef.current = false;
    try { localStorage.setItem(scenePromptsPendingKey(claymationCampaignId), String(scenePromptsStartedAtRef.current)); } catch {}
    setIsGeneratingScenePrompts(true);
    try {
      const res = await fetch(`/api/claymation-campaigns/${claymationCampaignId}/generate-scene-prompts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: "Failed to start" }));
        throw new Error(err.message || "Failed to start");
      }
      toast({ title: "Scene prompts queued", description: `${ready.length} script${ready.length === 1 ? "" : "s"} queued.` });
      queryClient.invalidateQueries({ queryKey: ["/api/claymation-campaigns", claymationCampaignId, "scripts"] });
    } catch (e: any) {
      toast({ title: "Scene prompt generation failed", description: e?.message || String(e), variant: "destructive" });
      setIsGeneratingScenePrompts(false);
      try { localStorage.removeItem(scenePromptsPendingKey(claymationCampaignId)); } catch {}
    }
  }, [scripts, claymationCampaignId, isGeneratingScenePrompts, toast]);

  // Cancel an in-flight scene-prompt generation. The server resets pending
  // scripts to idle and stops the background worker at its next checkpoint;
  // the user can then regenerate immediately.
  const [isCancellingScenePrompts, setIsCancellingScenePrompts] = useState(false);
  const cancelScenePrompts = useCallback(async () => {
    if (!claymationCampaignId || isCancellingScenePrompts) return;
    setIsCancellingScenePrompts(true);
    try {
      await apiRequest("POST", `/api/claymation-campaigns/${claymationCampaignId}/cancel-scene-prompts`);
      setIsGeneratingScenePrompts(false);
      scenePromptsStartedAtRef.current = 0;
      scenePromptSawPendingRef.current = false;
      try { localStorage.removeItem(scenePromptsPendingKey(claymationCampaignId)); } catch {}
      toast({ title: "Scene prompt generation cancelled" });
      queryClient.invalidateQueries({ queryKey: ["/api/claymation-campaigns", claymationCampaignId, "scripts"] });
    } catch (e: any) {
      toast({ title: "Cancel failed", description: e?.message || String(e), variant: "destructive" });
    } finally {
      setIsCancellingScenePrompts(false);
    }
  }, [claymationCampaignId, isCancellingScenePrompts, toast]);

  // Resume scene-prompt polling after a reload / navigation while a generation
  // started in a previous mount is still within its window.
  useEffect(() => {
    if (!claymationCampaignId) return;
    let ts = 0;
    try { ts = Number(localStorage.getItem(scenePromptsPendingKey(claymationCampaignId)) || 0); } catch {}
    if (ts && Date.now() - ts < SCENE_PROMPTS_MAX_MS) {
      scenePromptsStartedAtRef.current = ts;
      setIsGeneratingScenePrompts(true);
    } else if (ts) {
      try { localStorage.removeItem(scenePromptsPendingKey(claymationCampaignId)); } catch {}
    }
  }, [claymationCampaignId]);

  // Resume the loading state whenever the server still reports a target script
  // as "pending" (e.g. after a reload with no localStorage marker). This makes
  // the in-flight state survive reloads on server status alone.
  useEffect(() => {
    if (!scripts || isGeneratingScenePrompts) return;
    const anyPending = scripts.some((s: any) =>
      s.status === "locked" && s.storyboardStatus === "ready" && s.scenePromptStatus === "pending"
    );
    if (anyPending) {
      scenePromptSawPendingRef.current = true;
      if (!scenePromptsStartedAtRef.current) scenePromptsStartedAtRef.current = Date.now();
      setIsGeneratingScenePrompts(true);
    }
  }, [scripts, isGeneratingScenePrompts]);

  // Stop scene-prompt polling deterministically from the server-side status:
  // once we've seen scripts go "pending" and none remain pending, the run has
  // resolved (ready / error / idle). A hard time cap is kept only as a safety
  // net in case status never appears (very old data, status write failure).
  useEffect(() => {
    if (!isGeneratingScenePrompts || !claymationCampaignId) return;
    const clearPending = () => {
      setIsGeneratingScenePrompts(false);
      scenePromptsStartedAtRef.current = 0;
      scenePromptSawPendingRef.current = false;
      try { localStorage.removeItem(scenePromptsPendingKey(claymationCampaignId)); } catch {}
    };
    let ts = 0;
    try { ts = Number(localStorage.getItem(scenePromptsPendingKey(claymationCampaignId)) || 0); } catch {}
    if (!ts) ts = scenePromptsStartedAtRef.current;
    const elapsed = ts ? Date.now() - ts : 0;
    if (ts && elapsed >= SCENE_PROMPTS_MAX_MS) { clearPending(); return; }

    const readyScripts = (scripts || []).filter((s: any) => s.status === "locked" && s.storyboardImageUrl && s.storyboardStatus === "ready");
    const anyPending = readyScripts.some((s: any) => s.scenePromptStatus === "pending");
    if (anyPending) scenePromptSawPendingRef.current = true;
    // Stale-marker guard: if we resumed from a localStorage marker but the
    // server reports nothing pending and we never saw it become pending, the
    // run isn't actually in flight — stop after a short grace (the server marks
    // pending well within this window on a real run, before the POST returns).
    const SCENE_PROMPTS_GRACE_MS = 30_000;
    if (!scenePromptSawPendingRef.current && !anyPending && elapsed > SCENE_PROMPTS_GRACE_MS) {
      clearPending();
      return;
    }
    // The server has resolved every script for this run.
    if (scenePromptSawPendingRef.current && !anyPending) {
      const errored = readyScripts.filter((s: any) => s.scenePromptStatus === "error");
      if (errored.length) {
        toast({
          title: "Scene prompt generation failed",
          description: (errored[0] as any).scenePromptError || "Generation failed. Try again.",
          variant: "destructive",
          duration: 12000,
        });
      }
      clearPending();
      return;
    }
    const remaining = ts ? Math.max(0, SCENE_PROMPTS_MAX_MS - elapsed) : SCENE_PROMPTS_MAX_MS;
    const timer = setTimeout(clearPending, remaining);
    return () => clearTimeout(timer);
  }, [scripts, isGeneratingScenePrompts, claymationCampaignId, toast]);

  const extractAllPanels = useCallback(async () => {
    if (!scripts || isExtractingPanels) return;
    const ready = scripts.filter((s: any) => s.status === "locked" && s.avatarId && s.storyboardImageUrl && s.storyboardStatus === "ready");
    if (ready.length === 0) {
      toast({ title: "No storyboards ready", description: "Generate storyboards first.", variant: "destructive" });
      return;
    }
    setIsExtractingPanels(true);
    try {
      await Promise.all(ready.map((s: any) =>
        fetch(`/api/claymation-scripts/${s.id}/extract-panels`, { method: "POST", credentials: "include" }).catch(() => undefined)
      ));
      toast({ title: "Panel extraction queued", description: `${ready.length} script${ready.length === 1 ? "" : "s"} re-slicing.` });
      queryClient.invalidateQueries({ queryKey: ["/api/claymation-campaigns", claymationCampaignId, "scripts"] });
    } catch (e: any) {
      toast({ title: "Panel extraction failed", description: e?.message || String(e), variant: "destructive" });
    } finally {
      setIsExtractingPanels(false);
    }
  }, [scripts, claymationCampaignId, isExtractingPanels, toast]);

  const generateAllVideos = useCallback(async () => {
    if (!scripts || isGeneratingVideos) return;
    const why = (s: any): string | null => {
      if (s.status !== "locked") return `status=${s.status} (need: locked)`;
      if (!s.avatarId) return "no avatar";
      if (!s.scenePrompts || !Array.isArray(s.scenePrompts) || s.scenePrompts.length === 0) return "no scenePrompts";
      if (!(s.scenePrompts as any[]).some((p: any) => p.prompt)) return "scenePrompts exist but no prompt text";
      return null;
    };
    const readyScripts = scripts.filter(s => why(s) === null);
    console.log("[generateAllVideos] total:", scripts.length, "ready:", readyScripts.length,
      scripts.map((s: any) => ({ id: s.id, title: s.title, reason: why(s) })));
    if (readyScripts.length === 0) {
      const reasons = scripts.map((s: any) => `${s.title || s.id.slice(0,6)}: ${why(s)}`).join("; ");
      toast({ title: "No prompts ready", description: reasons || "Generate scene prompts first", variant: "destructive" });
      return;
    }

    setIsGeneratingVideos(true);
    const progress: Record<string, "pending" | "generating" | "done" | "error"> = {};
    readyScripts.forEach(s => { progress[s.id] = "pending"; });
    setGenProgress({ ...progress });

    const CONCURRENCY = 2;
    let doneCount = 0;
    const queue = [...readyScripts];

    async function processScript(script: typeof readyScripts[0]) {
      progress[script.id] = "generating";
      setGenProgress({ ...progress });
      try {
        console.log(`[generateAllVideos] POST /api/claymation-scripts/${script.id}/generate-scene-videos`);
        const res = await fetch(`/api/claymation-scripts/${script.id}/generate-scene-videos`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });
        const bodyText = await res.text();
        console.log(`[generateAllVideos] ${script.id} → HTTP ${res.status}`, bodyText.slice(0, 600));
        if (!res.ok) {
          let msg = `HTTP ${res.status}`;
          try { const j = JSON.parse(bodyText); if (j?.message) msg = j.message; } catch { if (bodyText) msg += `: ${bodyText.slice(0, 200)}`; }
          throw new Error(msg);
        }
        // 202 = queued. Actual completion arrives via the scripts polling
        // query (which writes sceneVideos[i].videoUrl as each render finishes).
        progress[script.id] = "done";
        doneCount++;
      } catch (err: any) {
        progress[script.id] = "error";
        const msg = err?.message || String(err) || "Unknown error";
        console.error(`[generateAllVideos] ${script.id} failed:`, msg);
        toast({ title: `Video gen failed for "${script.title || "Script"}"`, description: msg, variant: "destructive", duration: 15000 });
      }
      setGenProgress({ ...progress });
    }

    let idx = 0;
    async function worker() {
      while (idx < queue.length) {
        const script = queue[idx++];
        await processScript(script);
      }
    }
    await Promise.allSettled(Array.from({ length: CONCURRENCY }, () => worker()));

    queryClient.invalidateQueries({ queryKey: ["/api/claymation-campaigns", claymationCampaignId, "scripts"] });
    toast({ title: `Video generation started`, description: `${doneCount}/${readyScripts.length} script(s) queued. Videos will appear here as kie.ai finishes each scene (~1–3 min each).`, duration: 8000 });
    setIsGeneratingVideos(false);
  }, [scripts, claymationCampaignId, isGeneratingVideos, toast]);

  // Pause scene-video generation for this campaign. The server stops launching
  // new clips (in-flight ones finish) and marks the rest "paused".
  const [isPausingVideos, setIsPausingVideos] = useState(false);
  const pauseSceneVideos = useCallback(async () => {
    if (isPausingVideos) return;
    setIsPausingVideos(true);
    try {
      const res = await apiRequest("POST", `/api/claymation-campaigns/${claymationCampaignId}/pause-scene-videos`);
      const data = await res.json().catch(() => ({}));
      queryClient.invalidateQueries({ queryKey: ["/api/claymation-campaigns", claymationCampaignId, "scripts"] });
      toast({ title: "Generation paused", description: `${data?.scenesPaused ?? 0} pending scene${data?.scenesPaused === 1 ? "" : "s"} held. Restart anytime to finish them.` });
    } catch (e: any) {
      toast({ title: "Couldn't pause", description: e?.message || String(e), variant: "destructive" });
    } finally {
      setIsPausingVideos(false);
    }
  }, [claymationCampaignId, isPausingVideos, toast]);

  // Restart (resume) paused/incomplete scene-video generation for this campaign.
  const [isRestartingVideos, setIsRestartingVideos] = useState(false);
  const restartSceneVideos = useCallback(async () => {
    if (isRestartingVideos) return;
    setIsRestartingVideos(true);
    try {
      const res = await apiRequest("POST", `/api/claymation-campaigns/${claymationCampaignId}/resume-scene-videos`);
      const data = await res.json().catch(() => ({}));
      queryClient.invalidateQueries({ queryKey: ["/api/claymation-campaigns", claymationCampaignId, "scripts"] });
      toast({ title: "Generation restarted", description: `Resuming ${data?.scriptCount ?? 0} script(s). Clips will appear as kie.ai finishes each scene.`, duration: 8000 });
    } catch (e: any) {
      toast({ title: "Couldn't restart", description: e?.message || String(e), variant: "destructive" });
    } finally {
      setIsRestartingVideos(false);
    }
  }, [claymationCampaignId, isRestartingVideos, toast]);

  // Tracks per-script VO submissions optimistically so the button shows a
  // spinner the instant it's clicked, instead of waiting ~3s for the next
  // server poll to reflect voiceOverStatus="generating".
  const [voSubmitting, setVoSubmitting] = useState<Set<string>>(new Set());
  const generateVoiceOver = useCallback(async (scriptId: string) => {
    console.log("[VO] generateVoiceOver click for script:", scriptId);
    setVoSubmitting((prev) => { const next = new Set(prev); next.add(scriptId); return next; });
    try {
      const res = await fetch(`/api/claymation-scripts/${scriptId}/generate-voiceover`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      console.log("[VO] HTTP", res.status, "for", scriptId);
      const bodyText = await res.text();
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try { const j = JSON.parse(bodyText); if (j?.message) msg = j.message; } catch { if (bodyText) msg += `: ${bodyText.slice(0, 200)}`; }
        throw new Error(msg);
      }
      toast({ title: "Voice-over started", description: "ElevenLabs is rendering the audio. It usually takes 10–30 seconds." });
      queryClient.invalidateQueries({ queryKey: ["/api/claymation-campaigns", claymationCampaignId, "scripts"] });
    } catch (err: any) {
      toast({ title: "Voice-over failed", description: err?.message || "Unknown error", variant: "destructive", duration: 12000 });
    } finally {
      setVoSubmitting((prev) => { const next = new Set(prev); next.delete(scriptId); return next; });
    }
  }, [claymationCampaignId, toast]);

  const [isGeneratingVoiceOvers, setIsGeneratingVoiceOvers] = useState(false);
  const voPrevCountsRef = useRef<{ generating: number; ready: number; failed: number } | null>(null);
  useEffect(() => {
    if (!scripts) return;
    let generating = 0, ready = 0, failed = 0;
    scripts.forEach((s: any) => {
      if (s.voiceOverStatus === "generating") generating++;
      else if (s.voiceOverStatus === "ready") ready++;
      else if (s.voiceOverStatus === "failed") failed++;
    });
    const prev = voPrevCountsRef.current;
    if (prev && prev.generating > 0 && generating === 0) {
      const newlyReady = ready - prev.ready;
      const newlyFailed = failed - prev.failed;
      if (newlyReady > 0 || newlyFailed > 0) {
        toast({
          title: newlyFailed === 0 ? "Voice-overs ready" : `Voice-overs finished`,
          description: `${newlyReady} succeeded${newlyFailed ? `, ${newlyFailed} failed` : ""}.`,
          variant: newlyFailed > 0 && newlyReady === 0 ? "destructive" : undefined,
          duration: 6000,
        });
      }
    }
    voPrevCountsRef.current = { generating, ready, failed };
  }, [scripts, toast]);

  const generateAllVoiceOvers = useCallback(async () => {
    console.log("[VO] generateAllVoiceOvers click — scripts:", scripts?.length, "busy:", isGeneratingVoiceOvers);
    if (!scripts || isGeneratingVoiceOvers) { console.warn("[VO] aborted: no scripts or already running"); return; }
    const eligible = scripts.filter((s: any) => {
      try {
        const parsed = typeof s.fullScript === "string" ? JSON.parse(s.fullScript) : s.fullScript;
        return Array.isArray(parsed?.scenes) && parsed.scenes.some((sc: any) => typeof sc?.narration === "string" && sc.narration.trim().length > 0);
      } catch { return false; }
    });
    console.log("[VO] eligible scripts:", eligible.length, eligible.map((s: any) => s.id));
    if (eligible.length === 0) {
      toast({ title: "No narration found", description: "None of the scripts contain scene narration text.", variant: "destructive" });
      return;
    }
    console.log("[VO] entering bulk worker setup");
    setIsGeneratingVoiceOvers(true);
    let ok = 0; let fail = 0;
    const CONCURRENCY = 3;
    let idx = 0;
    async function worker(wid: number) {
      console.log(`[VO] worker ${wid} start, idx=${idx}, eligible=${eligible.length}`);
      while (idx < eligible.length) {
        const i = idx++;
        const s = eligible[i];
        console.log(`[VO] worker ${wid} → fetch script ${s.id}`);
        try {
          const res = await fetch(`/api/claymation-scripts/${s.id}/generate-voiceover`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
          });
          console.log(`[VO] worker ${wid} ← HTTP ${res.status} for ${s.id}`);
          if (!res.ok) {
            const t = await res.text().catch(() => "");
            console.error(`[VO] ${s.id} HTTP ${res.status}:`, t.slice(0, 300));
            fail++;
          } else {
            ok++;
          }
        } catch (e: any) {
          console.error(`[VO] ${s.id} threw:`, e?.message || e, e);
          fail++;
        }
      }
      console.log(`[VO] worker ${wid} done`);
    }
    await Promise.allSettled(Array.from({ length: CONCURRENCY }, (_, wid) => worker(wid)));
    console.log(`[VO] all workers done — ok=${ok} fail=${fail}`);
    queryClient.invalidateQueries({ queryKey: ["/api/claymation-campaigns", claymationCampaignId, "scripts"] });
    toast({
      title: "Voice-overs queued",
      description: `${ok} script${ok === 1 ? "" : "s"} started${fail ? `, ${fail} failed` : ""}. ElevenLabs renders each in ~10–30 s.`,
      duration: 8000,
    });
    setIsGeneratingVoiceOvers(false);
  }, [scripts, claymationCampaignId, isGeneratingVoiceOvers, toast]);

  // "Generate All" auto-pilot driver. Reads the latest pipeline snapshot and,
  // while active, fires the next not-done step once the pipeline is idle. Waits
  // for the fired step to finish before advancing, and stops on completion or if
  // the next step is blocked/gated/failed (so a persistent failure can't loop).
  useEffect(() => {
    if (!autoRunPipeline) {
      autoRunFiredRef.current = null;
      return;
    }
    const snap = pipelineRef.current;
    if (!snap || snap.length === 0) return;

    // If we already fired a step, wait until it's done before moving on.
    if (autoRunFiredRef.current) {
      const fired = snap.find((s) => s.key === autoRunFiredRef.current);
      if (fired && fired.done) {
        autoRunFiredRef.current = null;
      } else if (fired && fired.failed && !fired.busy) {
        setAutoRunPipeline(false);
        return;
      } else {
        return; // still running / pending
      }
    }

    if (snap.some((s) => s.busy)) return; // let any in-flight work settle

    // Advance to the first step that still has work to do, stepping over any
    // "skip" steps (nothing to do, e.g. Voice-Over with no narration) so they
    // don't stall the chain before later steps run.
    const next = snap.find((s) => !s.done && !s.skip);
    if (!next) {
      setAutoRunPipeline(false); // whole pipeline complete
      return;
    }
    if (next.disabled || next.gated || next.failed) {
      setAutoRunPipeline(false); // can't proceed automatically
      return;
    }
    autoRunFiredRef.current = next.key;
    next.onClick();
  }, [
    autoRunPipeline,
    scripts,
    plans,
    isGeneratingConcepts,
    conceptFailed,
    rendersByScript,
    isGeneratingBeats,
    isGeneratingPrompts,
    isGeneratingScenePrompts,
    isGeneratingVideos,
    isGeneratingVoiceOvers,
    renderAllMutation.isPending,
    captionAllMutation.isPending,
  ]);

  const startEditingScript = useCallback((script: ClaymationScript) => {
    setEditingScriptId(script.id);
    setEditingTitle(script.title || "");
    try {
      const parsed = typeof script.fullScript === "string" ? JSON.parse(script.fullScript) : script.fullScript;
      if (parsed?.scenes && Array.isArray(parsed.scenes)) {
        setEditingScenes(parsed.scenes.map((s: any) => ({ ...s })));
      } else {
        setEditingScenes(null);
      }
    } catch {
      setEditingScenes(null);
    }
  }, []);

  const saveEditingScript = useCallback((script: ClaymationScript) => {
    if (!editingScenes) return;
    const parsed = typeof script.fullScript === "string" ? JSON.parse(script.fullScript) : script.fullScript;
    const updatedFullScript = { ...parsed, script_title: editingTitle, scenes: editingScenes };
    const hook = editingScenes[0]?.script || "";
    const lastScene = editingScenes[editingScenes.length - 1];
    const cta = lastScene?.script || "";
    const bodyParts = editingScenes.slice(1, -1).map((s: any) => s.script || "").filter(Boolean);
    updateScriptMutation.mutate({
      scriptId: script.id,
      data: {
        title: editingTitle,
        hook,
        body: bodyParts.join("\n\n"),
        cta,
        fullScript: JSON.stringify(updatedFullScript),
      },
    });
  }, [editingScenes, editingTitle, updateScriptMutation]);

  const persona = campaign?.personaId ? personas?.find(p => p.id === campaign.personaId) : null;
  const product = campaign?.productId ? products?.find(p => p.id === campaign.productId) : null;
  const narrativeAngle = campaign?.narrativeAngleId ? narrativeAnglesData?.find(a => a.id === campaign.narrativeAngleId) : null;
  const selectedCharacterIds: string[] = Array.isArray((campaign?.notes as any)?.selectedCharacterIds)
    ? ((campaign!.notes as any).selectedCharacterIds as string[])
    : [];
  const selectedCharacters = (personaCharacters || []).filter((c: any) => selectedCharacterIds.includes(c.id));

  const selectedAvatars = avatars?.filter(a => a.status === "selected") || [];
  const generatedAvatars = avatars?.filter(a => a.status === "generated") || [];
  const rejectedAvatars = avatars?.filter(a => a.status === "rejected") || [];
  const targetCount = campaign?.avatarCount || 5;
  const isFull = selectedAvatars.length >= targetCount;
  const isLocked = selectedCharacters.length > 0 || (isFull && generatedAvatars.length === 0 && rejectedAvatars.length === 0);

  const scriptsLocked = scripts && scripts.length > 0 && scripts.every(s => s.status === "locked");
  // Concept-first (UGC parity): concepts come before scripts. A campaign "has
  // concepts" once at least one plan exists.
  const hasPlans = !!plans && plans.length > 0;

  // Single entry point for kicking off concept generation. The synchronous
  // in-flight ref dedupes overlapping triggers — the auto-setup effect and the
  // "Generate All" auto-pilot can both target the concept step in the same
  // render tick, and a button can be clicked while one is already running.
  // Automatic callers pass { auto: true } so they never silently regenerate an
  // existing concept; only an explicit user click may replace concepts.
  const triggerConceptGeneration = useCallback((opts?: { auto?: boolean }) => {
    if (conceptGenInFlightRef.current || isGeneratingConcepts || generateScriptPlansMutation.isPending) return;
    if (opts?.auto && hasPlans) return;
    conceptGenInFlightRef.current = true;
    generateScriptPlansMutation.mutate();
  }, [hasPlans, isGeneratingConcepts, generateScriptPlansMutation.mutate, generateScriptPlansMutation.isPending]);

  // Concept-first flow: once the cast is locked in, automatically generate the
  // campaign CONCEPTS (the first pipeline step) from the brand/persona/product/
  // angle inputs. Scripts are written from these concepts in a later step. The
  // route deletes+rebuilds plans, so this fires once per campaign (covers a
  // fresh cast lock and reopening a campaign locked before concepts existed).
  const autoSetupScriptsRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isLocked || plansLoading || scriptsLoading) return;
    if (hasPlans || (scripts && scripts.length > 0)) return;
    if (isGeneratingConcepts || generateScriptPlansMutation.isPending) return;
    if (autoSetupScriptsRef.current === claymationCampaignId) return;
    autoSetupScriptsRef.current = claymationCampaignId;
    triggerConceptGeneration({ auto: true });
  }, [isLocked, plansLoading, scriptsLoading, hasPlans, scripts, isGeneratingConcepts, generateScriptPlansMutation.isPending, claymationCampaignId, triggerConceptGeneration]);

  if (campaignLoading) {
    return (
      <div className="p-8 max-w-4xl mx-auto space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full rounded-2xl" />
        <Skeleton className="h-24 w-full rounded-2xl" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="p-8 max-w-4xl mx-auto">
        <p className="text-muted-foreground">Campaign not found.</p>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-3 fade-in-up">
        <Button
          variant="ghost"
          size="sm"
          className="rounded-full"
          onClick={() => navigate(`/brands/${brandId}/claymation-campaigns`)}
          data-testid="button-back-claymation"
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
      </div>

      <div className="fade-in-up rounded-2xl overflow-hidden border border-border/40 bg-card" data-testid="card-claymation-campaign-overview">
        <div className="relative bg-gradient-to-br from-violet-600/[0.08] via-violet-500/[0.04] to-blue-500/[0.06] px-8 py-7">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(139,92,246,0.10),transparent_70%)]" />
          <div className="relative flex items-start gap-6">
            {(() => {
              const imgs = product && Array.isArray(product.adgenImageRefs) ? product.adgenImageRefs as string[] : [];
              return imgs.length > 0 ? (
                <img src={optimizedImg(imgs[0], 224, 80)} alt={product?.name} loading="lazy" className="w-28 h-28 rounded-2xl object-cover shrink-0 border-2 border-white/70 shadow-xl ring-1 ring-black/5" />
              ) : (
                <div className="w-28 h-28 rounded-2xl bg-gradient-to-br from-violet-500/20 to-violet-600/30 flex items-center justify-center shrink-0 shadow-xl ring-1 ring-violet-500/10">
                  <Clapperboard className="h-10 w-10 text-violet-500" />
                </div>
              );
            })()}
            <div className="flex-1 min-w-0 pt-1">
              <div className="flex items-start justify-between gap-4 mb-2.5">
                <h1 className="text-lg font-semibold tracking-tight leading-snug" data-testid="text-claymation-detail-name">{campaign.name}</h1>
                <span className={`shrink-0 inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider ${statusColors[campaign.status] || statusColors.draft}`}>
                  {campaign.status}
                </span>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                {persona && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-500/10 px-2.5 py-0.5 text-[11px] font-medium text-violet-700 ring-1 ring-violet-500/15">
                    <Users className="h-3 w-3" /> {persona.name}
                  </span>
                )}
                {product && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-500/10 px-2.5 py-0.5 text-[11px] font-medium text-blue-700 ring-1 ring-blue-500/15">
                    <Package className="h-3 w-3" /> {product.name}
                  </span>
                )}
                {narrativeAngle && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/10 px-2.5 py-0.5 text-[11px] font-medium text-amber-700 ring-1 ring-amber-500/15">
                    <BookOpen className="h-3 w-3" /> {narrativeAngle.name}
                  </span>
                )}
              </div>
              {campaign.concept && (
                <p className="text-[13px] text-muted-foreground leading-relaxed max-w-2xl mt-3">{campaign.concept}</p>
              )}
            </div>
            {selectedCharacters.length > 0 && (
              <div className="hidden md:flex flex-col items-end gap-1.5 shrink-0" data-testid="header-selected-models">
                <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider font-medium">
                  Cast · {selectedCharacters.length}
                </p>
                <div className="flex items-center gap-1.5">
                  {selectedCharacters.slice(0, 5).map((c: any) => (
                    <div
                      key={c.id}
                      className="w-12 h-16 rounded-lg ring-2 ring-white overflow-hidden bg-muted shadow-md"
                      title={c.name}
                    >
                      {c.avatarUrl ? (
                        <img src={c.avatarUrl} alt={c.name} className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <UserCircle className="h-5 w-5 text-muted-foreground/50" />
                        </div>
                      )}
                    </div>
                  ))}
                  {selectedCharacters.length > 5 && (
                    <div className="w-12 h-16 rounded-lg ring-2 ring-white bg-muted text-[11px] font-semibold flex items-center justify-center shadow-md text-muted-foreground">
                      +{selectedCharacters.length - 5}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-border/30 bg-muted/[0.03]">
          <div className="grid grid-cols-3 divide-x divide-border/20">
            <div className="px-5 py-4">
              <p className="text-[10px] text-muted-foreground/50 uppercase tracking-wider font-medium mb-1.5">Template</p>
              <div className="flex items-center gap-2">
                <Video className="h-3.5 w-3.5 text-amber-500/70 shrink-0" />
                <span className="text-[13px] font-medium truncate">{campaign.videoStyle || "—"}</span>
              </div>
            </div>
            <div className="px-5 py-4">
              <p className="text-[10px] text-muted-foreground/50 uppercase tracking-wider font-medium mb-1.5">Videos</p>
              <div className="flex items-center gap-2">
                <Film className="h-3.5 w-3.5 text-violet-500/70 shrink-0" />
                <span className="text-[13px] font-medium">{campaign.videoCount ?? "—"}</span>
              </div>
            </div>
            <div className="px-5 py-4">
              <p className="text-[10px] text-muted-foreground/50 uppercase tracking-wider font-medium mb-1.5">Cast</p>
              <div className="flex items-center gap-2">
                <UserCircle className="h-3.5 w-3.5 text-blue-500/70 shrink-0" />
                <span className="text-[13px] font-medium">{selectedCharacters.length || campaign.avatarCount || "—"}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {false && !scriptsLocked && (<div className="fade-in-up-delay-1 space-y-4" data-testid="section-avatars">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold tracking-tight flex items-center gap-2">
            <ImageIcon className="h-4 w-4 text-violet-500" />
            Starting Frame Avatars
            {!!avatars && (avatars?.length ?? 0) > 0 && (
              <span className="text-xs text-muted-foreground font-normal">
                ({selectedAvatars.length} selected / {avatars?.length ?? 0} total)
              </span>
            )}
          </h2>
          <div className="flex items-center gap-2">
            {hasPreviousAvatars && (
              <div className="flex items-center bg-muted/50 rounded-full p-0.5 gap-0.5">
                <button
                  onClick={() => setAvatarTab("current")}
                  className={`text-[11px] font-medium px-3 py-1 rounded-full transition-colors ${avatarTab === "current" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                  data-testid="tab-avatars-current"
                >
                  Current
                </button>
                <button
                  onClick={() => setAvatarTab("previous")}
                  className={`text-[11px] font-medium px-3 py-1 rounded-full transition-colors ${avatarTab === "previous" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                  data-testid="tab-avatars-previous"
                >
                  Previously Used
                  <span className="ml-1 text-[9px] bg-violet-500/10 text-violet-600 dark:text-violet-400 rounded-full px-1.5">
                    {previousAvatarGroups?.reduce((sum, g) => sum + g.avatars.length, 0) || 0}
                  </span>
                </button>
              </div>
            )}
            {!isLocked && avatarTab === "current" && (
              <Button
                size="sm"
                onClick={() => generateAvatarsMutation.mutate()}
                disabled={generateAvatarsMutation.isPending}
                className="rounded-full"
                data-testid="button-generate-avatars"
              >
                {generateAvatarsMutation.isPending ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-3.5 w-3.5 mr-1" />
                    Generate Avatars
                  </>
                )}
              </Button>
            )}
          </div>
        </div>

        {avatarTab === "previous" && hasPreviousAvatars && (
          <div className="space-y-5">
            <p className="text-xs text-muted-foreground/60">
              These avatars were selected in previous campaigns with the same persona and template. Click "Use This" to import one into the current campaign.
            </p>
            {previousAvatarGroups!.map(group => (
              <div key={group.campaignId} className="space-y-2">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground/50 font-semibold px-1">
                  From: {group.campaignName}
                </p>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                  {group.avatars.map(avatar => {
                    const alreadyImported = avatars?.some(a => a.imageUrl === avatar.imageUrl) || false;
                    return (
                      <div key={avatar.id} className={`group relative rounded-xl overflow-hidden border transition-all ${alreadyImported ? "border-emerald-500/40 opacity-60" : "border-border/40 hover:border-violet-500/40"}`} data-testid={`avatar-previous-${avatar.id}`}>
                        <img
                          src={optimizedImg(avatar.imageUrl, 300, 75)}
                          alt="Previously used avatar"
                          loading="lazy"
                          className="w-full aspect-[9/16] object-cover"
                        />
                        {alreadyImported ? (
                          <div className="absolute bottom-0 left-0 right-0 bg-emerald-500/90 text-white text-[10px] font-semibold text-center py-1">
                            Already Added
                          </div>
                        ) : (
                          <div className="absolute bottom-0 left-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => importAvatarMutation.mutate(avatar)}
                              disabled={importAvatarMutation.isPending || isFull}
                              className="w-full bg-violet-600/90 hover:bg-violet-700 text-white text-[10px] font-semibold text-center py-1.5 transition-colors disabled:opacity-50"
                              data-testid={`button-import-avatar-${avatar.id}`}
                            >
                              {importAvatarMutation.isPending ? "Importing..." : isFull ? "Selection Full" : "Use This"}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {avatarTab === "current" && generateAvatarsMutation.isPending && (
          <Card className="border-violet-500/20 bg-violet-500/[0.03]">
            <CardContent className="flex items-center gap-3 py-6 justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-violet-500" />
              <p className="text-sm text-muted-foreground">
                Generating {((campaign?.avatarCount || 5)) * 2} avatar images using persona and ad unit prompt...
              </p>
            </CardContent>
          </Card>
        )}

        {avatarTab === "current" && (avatarsLoading ? (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="aspect-[9/16] w-full rounded-xl" />)}
          </div>
        ) : !!avatars && (avatars?.length ?? 0) > 0 ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3" data-testid="avatar-progress-bar">
              <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${isFull ? "bg-emerald-500" : "bg-violet-500"}`}
                  style={{ width: `${Math.min((selectedAvatars.length / targetCount) * 100, 100)}%` }}
                />
              </div>
              <span className={`text-sm font-semibold whitespace-nowrap ${isFull ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`} data-testid="avatar-progress-text">
                {selectedAvatars.length} / {targetCount} selected
              </span>
              {isFull && (generatedAvatars.length > 0 || rejectedAvatars.length > 0) && (
                <Button
                  size="sm"
                  className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={() => finalizeAvatarsMutation.mutate()}
                  disabled={finalizeAvatarsMutation.isPending}
                  data-testid="button-finalize-avatars"
                >
                  {finalizeAvatarsMutation.isPending ? (
                    <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Saving...</>
                  ) : (
                    <><Lock className="h-3.5 w-3.5 mr-1" /> Lock In Avatars</>
                  )}
                </Button>
              )}
              {isFull && generatedAvatars.length === 0 && rejectedAvatars.length === 0 && (
                <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full flex items-center gap-1">
                  <Lock className="h-3 w-3" /> Locked
                </span>
              )}
            </div>

            {selectedAvatars.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wider text-emerald-600 dark:text-emerald-400 font-semibold mb-2 flex items-center gap-1">
                  <Check className="h-3 w-3" /> Selected ({selectedAvatars.length})
                </p>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                  {selectedAvatars.map(avatar => (
                    <div key={avatar.id} className="group relative rounded-xl overflow-hidden ring-2 ring-emerald-500 shadow-lg" data-testid={`avatar-selected-${avatar.id}`}>
                      <img
                        src={optimizedImg(avatar.imageUrl, 300, 75)}
                        alt="Avatar"
                        loading="lazy"
                        className="w-full aspect-[9/16] object-cover cursor-pointer"
                        onClick={() => setLightboxAvatar(avatar)}
                      />
                      <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          className="w-6 h-6 rounded-full bg-amber-500/90 text-white flex items-center justify-center hover:bg-amber-600 transition-colors"
                          onClick={() => updateAvatarMutation.mutate({ id: avatar.id, status: "generated" })}
                          title="Deselect"
                          data-testid={`button-deselect-avatar-${avatar.id}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                        <button
                          className="w-6 h-6 rounded-full bg-red-500/90 text-white flex items-center justify-center hover:bg-red-600 transition-colors"
                          onClick={() => deleteAvatarMutation.mutate(avatar.id)}
                          title="Delete"
                          data-testid={`button-delete-avatar-${avatar.id}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-emerald-500/90 text-white text-[10px] font-semibold text-center py-0.5">
                        SELECTED
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {generatedAvatars.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground/70 font-semibold mb-2">
                  Generated ({generatedAvatars.length})
                </p>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                  {generatedAvatars.map(avatar => (
                    <div key={avatar.id} className="group relative rounded-xl overflow-hidden border border-border/40 hover:border-violet-500/40 transition-all" data-testid={`avatar-generated-${avatar.id}`}>
                      <img
                        src={optimizedImg(avatar.imageUrl, 300, 75)}
                        alt="Avatar"
                        loading="lazy"
                        className="w-full aspect-[9/16] object-cover cursor-pointer"
                        onClick={() => setLightboxAvatar(avatar)}
                      />
                      <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          className={`w-6 h-6 rounded-full text-white flex items-center justify-center transition-colors ${isFull ? "bg-gray-400/70 cursor-not-allowed" : "bg-emerald-500/90 hover:bg-emerald-600"}`}
                          onClick={() => !isFull && updateAvatarMutation.mutate({ id: avatar.id, status: "selected" })}
                          title={isFull ? `${targetCount} avatars already selected` : "Select"}
                          disabled={isFull}
                          data-testid={`button-select-avatar-${avatar.id}`}
                        >
                          <Check className="h-3 w-3" />
                        </button>
                        <button
                          className="w-6 h-6 rounded-full bg-red-500/90 text-white flex items-center justify-center hover:bg-red-600 transition-colors"
                          onClick={() => updateAvatarMutation.mutate({ id: avatar.id, status: "rejected" })}
                          title="Reject"
                          data-testid={`button-reject-avatar-${avatar.id}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {rejectedAvatars.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wider text-red-400/70 font-semibold mb-2">
                  Rejected ({rejectedAvatars.length})
                </p>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                  {rejectedAvatars.map(avatar => (
                    <div key={avatar.id} className="group relative rounded-xl overflow-hidden border border-border/20 opacity-50 hover:opacity-80 transition-all" data-testid={`avatar-rejected-${avatar.id}`}>
                      <img
                        src={optimizedImg(avatar.imageUrl, 300, 75)}
                        alt="Avatar"
                        loading="lazy"
                        className="w-full aspect-[9/16] object-cover cursor-pointer grayscale"
                        onClick={() => setLightboxAvatar(avatar)}
                      />
                      <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          className="w-6 h-6 rounded-full bg-emerald-500/90 text-white flex items-center justify-center hover:bg-emerald-600 transition-colors"
                          onClick={() => updateAvatarMutation.mutate({ id: avatar.id, status: "generated" })}
                          title="Restore"
                          data-testid={`button-restore-avatar-${avatar.id}`}
                        >
                          <Check className="h-3 w-3" />
                        </button>
                        <button
                          className="w-6 h-6 rounded-full bg-red-500/90 text-white flex items-center justify-center hover:bg-red-600 transition-colors"
                          onClick={() => deleteAvatarMutation.mutate(avatar.id)}
                          title="Delete permanently"
                          data-testid={`button-permadelete-avatar-${avatar.id}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : !generateAvatarsMutation.isPending ? (
          <Card className="border-dashed border-border/50">
            <CardContent className="flex flex-col items-center justify-center py-10">
              <div className="w-10 h-10 rounded-xl bg-muted/50 flex items-center justify-center mb-3">
                <ImageIcon className="h-5 w-5 text-muted-foreground/40" />
              </div>
              <p className="text-sm text-muted-foreground">No avatars yet. Use the button above to generate starting frame images.</p>
            </CardContent>
          </Card>
        ) : null)}
      </div>)}

      {isLocked && !hasPlans && !scriptsLocked && (() => {
        const conceptTarget = campaign?.videoCount || 20;
        const generating = isGeneratingConcepts || generateScriptPlansMutation.isPending;
        return (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold flex items-center gap-2" data-testid="heading-concept-generator">
              <Lightbulb className="h-5 w-5 text-amber-500" />
              Concept Generator
              <span className="text-xs text-muted-foreground font-normal">
                ({conceptTarget} concept{conceptTarget === 1 ? "" : "s"})
              </span>
            </h2>
          </div>

          {generating && (
            <Card className="border-amber-500/20 bg-amber-500/[0.03]">
              <CardContent className="flex items-center gap-3 py-4 justify-center">
                <Loader2 className="h-4 w-4 animate-spin text-amber-500" />
                <p className="text-sm text-muted-foreground">
                  Generating {conceptTarget} concept{conceptTarget === 1 ? "" : "s"} from your brand, persona, product, and angle… This may take a minute.
                </p>
              </CardContent>
            </Card>
          )}

          {!generating && (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-8 text-center">
                <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center mb-2">
                  <Lightbulb className="h-5 w-5 text-amber-500/70" />
                </div>
                {conceptFailed ? (
                  <p className="text-sm text-red-600 dark:text-red-400 mb-3" data-testid="text-concept-error">
                    Concept generation failed. Please try again.
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground mb-3">
                    Generate {conceptTarget} campaign concept{conceptTarget === 1 ? "" : "s"} from your brand, persona, product, and angle. Scripts are written from these in the next step.
                  </p>
                )}
                <Button
                  size="sm"
                  onClick={() => triggerConceptGeneration()}
                  disabled={generateScriptPlansMutation.isPending}
                  className="rounded-full"
                  data-testid="button-generate-concepts"
                >
                  <Sparkles className="h-3.5 w-3.5 mr-1" /> {conceptFailed ? "Retry" : "Generate Concepts"}
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
        );
      })()}

      {isLocked && !scriptsLocked && scripts && scripts.length > 0 && (
        <div className="space-y-4">
          {scriptsLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-24 w-full rounded-xl" />)}
            </div>
          ) : scripts && scripts.length > 0 ? (
            <div className="space-y-3">
              {selectedAvatars.map(avatar => {
                const avatarScripts = scripts.filter(s => s.avatarId === avatar.id);
                if (avatarScripts.length === 0) return null;
                return (
                  <div key={avatar.id} className="space-y-2">
                    <div className="flex items-center gap-2">
                      <img src={optimizedImg(avatar.imageUrl, 64, 70)} alt="Avatar" loading="lazy" className="w-8 h-8 rounded-full object-cover" />
                      <span className="text-sm font-semibold text-muted-foreground">{avatarScripts.length} scripts</span>
                    </div>
                    <div className="grid gap-2">
                      {avatarScripts.map((script, idx) => (
                        <Card key={script.id} className={`border-border/40 transition-colors ${expandedScriptId === script.id ? "border-violet-500/40" : "hover:border-violet-500/30"}`} data-testid={`card-script-${script.id}`}>
                          <CardContent className="py-3 px-4">
                            <div className="flex items-start justify-between gap-3 cursor-pointer" onClick={() => setExpandedScriptId(expandedScriptId === script.id ? null : script.id)}>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold truncate" data-testid={`text-script-title-${script.id}`}>
                                  {script.title || `Script ${idx + 1}`}
                                </p>
                                {expandedScriptId !== script.id && script.hook && (
                                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                    <span className="font-medium text-violet-500">Hook:</span> {script.hook}
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                {expandedScriptId === script.id && editingScriptId !== script.id && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="rounded-full h-7 w-7 p-0"
                                    onClick={(e) => { e.stopPropagation(); startEditingScript(script); }}
                                    data-testid={`button-edit-script-${script.id}`}
                                  >
                                    <Pencil className="h-3.5 w-3.5" />
                                  </Button>
                                )}
                                {editingScriptId === script.id && (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="rounded-full h-7 w-7 p-0 text-emerald-600"
                                      onClick={(e) => { e.stopPropagation(); saveEditingScript(script); }}
                                      disabled={updateScriptMutation.isPending}
                                      data-testid={`button-save-script-${script.id}`}
                                    >
                                      {updateScriptMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="rounded-full h-7 w-7 p-0 text-muted-foreground"
                                      onClick={(e) => { e.stopPropagation(); setEditingScriptId(null); setEditingScenes(null); }}
                                      data-testid={`button-cancel-edit-script-${script.id}`}
                                    >
                                      <X className="h-3.5 w-3.5" />
                                    </Button>
                                  </>
                                )}
                                <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ${
                                  script.status === "approved" ? "bg-emerald-500/10 text-emerald-600" :
                                  script.status === "rejected" ? "bg-red-500/10 text-red-600" :
                                  "bg-violet-500/10 text-violet-600"
                                }`} data-testid={`badge-script-status-${script.id}`}>
                                  {script.status}
                                </span>
                              </div>
                            </div>
                            {expandedScriptId === script.id && (
                              <div onClick={(e) => e.stopPropagation()} className="mt-3">
                                <ScriptRenderControls scriptId={script.id} hasScenes={allClipsReady(script)} />
                              </div>
                            )}
                            {(() => {
                              // Up to three storyboards per script. Claymation
                              // uses fixed 4s SeeDance clips, so one 9-panel grid
                              // is 36s: 36s → 1, 72s → 2, 108s → 3 storyboards.
                              // Each covers 9 scenes; the server marks the slots
                              // the ad length uses "generating" up front, so this
                              // status-driven filter shows exactly that many
                              // (unused slots stay idle/hidden).
                              const slots = [
                                { idx: 1, status: (script as any).storyboardStatus, url: (script as any).storyboardImageUrl, err: (script as any).storyboardError },
                                { idx: 2, status: (script as any).storyboardStatus2, url: (script as any).storyboardImageUrl2, err: (script as any).storyboardError2 },
                                { idx: 3, status: (script as any).storyboardStatus3, url: (script as any).storyboardImageUrl3, err: (script as any).storyboardError3 },
                              ].filter((s) => s.status && s.status !== "idle");
                              if (slots.length === 0) return null;
                              return (
                                <div onClick={(e) => e.stopPropagation()} className="mt-3 pt-3 border-t border-border/40 space-y-3">
                                  {slots.map((slot) => (
                                    <div key={slot.idx}>
                                      <div className="flex items-center justify-between mb-2">
                                        <p className="text-[10px] uppercase tracking-wider font-semibold text-violet-500">
                                          Storyboard {slot.idx} <span className="text-muted-foreground normal-case font-normal">· {`scenes ${(slot.idx - 1) * 9 + 1}–${slot.idx * 9}`}</span>
                                        </p>
                                        <div className="flex items-center gap-2">
                                          {slot.status === "generating" && (
                                            <span className="flex items-center gap-1 text-[10px] font-semibold text-amber-600 bg-amber-500/10 px-2 py-0.5 rounded-full" data-testid={`badge-storyboard-status-${slot.idx}-${script.id}`}>
                                              <Loader2 className="h-2.5 w-2.5 animate-spin" /> Generating
                                            </span>
                                          )}
                                          {slot.status === "ready" && (
                                            <span className="flex items-center gap-1 text-[10px] font-semibold text-emerald-600 bg-emerald-500/10 px-2 py-0.5 rounded-full" data-testid={`badge-storyboard-status-${slot.idx}-${script.id}`}>
                                              <Check className="h-2.5 w-2.5" /> Ready
                                            </span>
                                          )}
                                          {slot.status === "failed" && (
                                            <span className="flex items-center gap-1 text-[10px] font-semibold text-red-600 bg-red-500/10 px-2 py-0.5 rounded-full" data-testid={`badge-storyboard-status-${slot.idx}-${script.id}`}>
                                              <X className="h-2.5 w-2.5" /> Failed
                                            </span>
                                          )}
                                          {slot.status !== "generating" && (
                                            <button
                                              type="button"
                                              onClick={(e) => { e.stopPropagation(); regenerateStoryboardSlot(script.id, slot.idx); }}
                                              disabled={regenSlots.has(`${script.id}:${slot.idx}`)}
                                              className="flex items-center gap-1 text-[10px] font-semibold text-violet-600 hover:text-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                              data-testid={`button-regenerate-storyboard-${slot.idx}-${script.id}`}
                                              title="Regenerate this storyboard"
                                            >
                                              <RefreshCw className={`h-2.5 w-2.5 ${regenSlots.has(`${script.id}:${slot.idx}`) ? "animate-spin" : ""}`} /> Regenerate
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                      {slot.status === "generating" && (
                                        <div className="h-32 rounded-lg bg-muted/30 flex items-center justify-center">
                                          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                                        </div>
                                      )}
                                      {slot.status === "ready" && slot.url && (
                                        <img
                                          src={slot.url}
                                          alt={`Storyboard ${slot.idx}`}
                                          loading="lazy"
                                          className="w-full rounded-lg border border-border/40"
                                          data-testid={`img-storyboard-${slot.idx}-${script.id}`}
                                        />
                                      )}
                                      {slot.status === "failed" && (
                                        <p className="text-xs text-red-600" data-testid={`text-storyboard-error-${slot.idx}-${script.id}`}>{slot.err || "Generation failed"}</p>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              );
                            })()}
                            {(() => {
                              const voStatus = (script as any).voiceOverStatus;
                              const voUrl = (script as any).voiceOverUrl;
                              const voErr = (script as any).voiceOverError;
                              const hasNarration = (() => {
                                try {
                                  const parsed = typeof script.fullScript === "string" ? JSON.parse(script.fullScript) : (script.fullScript as any);
                                  return Array.isArray(parsed?.scenes) && parsed.scenes.some((s: any) => typeof s?.narration === "string" && s.narration.trim().length > 0);
                                } catch { return false; }
                              })();
                              if (!hasNarration) return null;
                              const isGenerating = voStatus === "generating" || voSubmitting.has(script.id) || isGeneratingVoiceOvers;
                              return (
                                <div onClick={(e) => e.stopPropagation()} className="mt-3 pt-3 border-t border-border/40">
                                  <div className="flex items-center justify-between mb-2">
                                    <p className="text-[10px] uppercase tracking-wider font-semibold text-violet-500">Voice-Over (ElevenLabs)</p>
                                    {voStatus === "generating" && (
                                      <span className="flex items-center gap-1 text-[10px] font-semibold text-amber-600 bg-amber-500/10 px-2 py-0.5 rounded-full" data-testid={`badge-voiceover-status-${script.id}`}>
                                        <Loader2 className="h-2.5 w-2.5 animate-spin" /> Generating
                                      </span>
                                    )}
                                    {voStatus === "ready" && (
                                      <span className="flex items-center gap-1 text-[10px] font-semibold text-emerald-600 bg-emerald-500/10 px-2 py-0.5 rounded-full" data-testid={`badge-voiceover-status-${script.id}`}>
                                        <Check className="h-2.5 w-2.5" /> Ready
                                      </span>
                                    )}
                                    {voStatus === "failed" && (
                                      <span className="flex items-center gap-1 text-[10px] font-semibold text-red-600 bg-red-500/10 px-2 py-0.5 rounded-full" data-testid={`badge-voiceover-status-${script.id}`}>
                                        <X className="h-2.5 w-2.5" /> Failed
                                      </span>
                                    )}
                                  </div>
                                  {voStatus === "ready" && voUrl && (
                                    <audio
                                      controls
                                      src={voUrl}
                                      className="w-full mb-2"
                                      data-testid={`audio-voiceover-${script.id}`}
                                    />
                                  )}
                                  {voStatus === "failed" && (
                                    <p className="text-xs text-red-600 mb-2" data-testid={`text-voiceover-error-${script.id}`}>{voErr || "Generation failed"}</p>
                                  )}
                                  <Button
                                    size="sm"
                                    variant={voStatus === "ready" ? "outline" : "default"}
                                    disabled={isGenerating}
                                    onClick={() => generateVoiceOver(script.id)}
                                    data-testid={`button-generate-voiceover-${script.id}`}
                                    className="h-7 text-xs"
                                  >
                                    {isGenerating ? (
                                      <><Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> Generating…</>
                                    ) : voStatus === "ready" ? (
                                      <><Sparkles className="h-3 w-3 mr-1.5" /> Regenerate Voice-Over</>
                                    ) : voStatus === "failed" ? (
                                      <><Sparkles className="h-3 w-3 mr-1.5" /> Retry Voice-Over</>
                                    ) : (
                                      <><Sparkles className="h-3 w-3 mr-1.5" /> Generate Voice-Over</>
                                    )}
                                  </Button>
                                </div>
                              );
                            })()}
                            {expandedScriptId === script.id && (
                              <div className="mt-3 pt-3 border-t border-border/40">
                                {editingScriptId === script.id && editingScenes ? (
                                  <div className="space-y-3">
                                    <div>
                                      <label className="text-[10px] uppercase tracking-wider font-semibold text-violet-500 mb-1 block">Title</label>
                                      <input
                                        type="text"
                                        value={editingTitle}
                                        onChange={(e) => setEditingTitle(e.target.value)}
                                        className="w-full text-sm font-semibold border border-border/60 rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-violet-500/30"
                                        data-testid={`input-script-title-${script.id}`}
                                      />
                                    </div>
                                    {editingScenes.map((scene: any, i: number) => (
                                      <div key={i} className="rounded-lg bg-muted/30 p-2.5">
                                        <div className="flex items-center gap-2 mb-1.5">
                                          <span className="text-[10px] font-bold uppercase bg-violet-500/10 text-violet-600 px-1.5 py-0.5 rounded-full">
                                            Scene {scene.scene_number || i + 1}
                                          </span>
                                          {scene.scene_role && (
                                            <span className="text-[10px] text-muted-foreground uppercase">{scene.scene_role}</span>
                                          )}
                                        </div>
                                        <Textarea
                                          value={scene.narration ?? scene.script ?? ""}
                                          onChange={(e) => {
                                            const updated = [...editingScenes];
                                            const key = "narration" in updated[i] ? "narration" : "script";
                                            updated[i] = { ...updated[i], [key]: e.target.value };
                                            setEditingScenes(updated);
                                          }}
                                          className="text-sm leading-relaxed min-h-[60px] resize-none"
                                          data-testid={`textarea-scene-${script.id}-${i}`}
                                        />
                                        {(scene.visual_purpose || scene.visual_direction || scene.direction) && (
                                          <p className="text-xs text-muted-foreground mt-1 italic">{scene.visual_purpose || scene.visual_direction || scene.direction}</p>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                ) : (() => {
                                  let parsed: any = null;
                                  let scenes: any[] = [];
                                  try {
                                    parsed = typeof script.fullScript === "string" ? JSON.parse(script.fullScript) : script.fullScript;
                                    if (parsed?.scenes && Array.isArray(parsed.scenes)) scenes = parsed.scenes;
                                  } catch {}
                                  const fullVo = typeof parsed?.voiceover_script === "string" ? parsed.voiceover_script.trim() : "";
                                  if (fullVo) {
                                    return (
                                      <div className="rounded-lg bg-muted/30 p-3">
                                        <p className="text-[10px] uppercase tracking-wider font-semibold text-violet-500 mb-1.5">Voice-over script</p>
                                        <p className="text-sm leading-relaxed whitespace-pre-wrap" data-testid={`text-vo-script-prelock-${script.id}`}>{fullVo}</p>
                                      </div>
                                    );
                                  }
                                  if (scenes.length > 0) {
                                    return (
                                      <div className="space-y-2">
                                        {scenes.map((scene: any, i: number) => (
                                          <div key={i} className="rounded-lg bg-muted/30 p-2.5">
                                            <div className="flex items-center gap-2 mb-1">
                                              <span className="text-[10px] font-bold uppercase bg-violet-500/10 text-violet-600 px-1.5 py-0.5 rounded-full">
                                                Scene {scene.scene_number || i + 1}
                                              </span>
                                              {scene.scene_role && (
                                                <span className="text-[10px] text-muted-foreground uppercase">{scene.scene_role}</span>
                                              )}
                                            </div>
                                            {(scene.narration || scene.script || scene.dialogue) && <p className="text-sm leading-relaxed">{scene.narration || scene.script || scene.dialogue}</p>}
                                            {(scene.visual_purpose || scene.visual_direction || scene.direction) && (
                                              <p className="text-xs text-muted-foreground mt-1 italic">{scene.visual_purpose || scene.visual_direction || scene.direction}</p>
                                            )}
                                            {scene.scene_action && (
                                              <p className="text-xs text-muted-foreground mt-1">{scene.scene_action}</p>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    );
                                  }
                                  return (
                                    <div className="space-y-3">
                                      {script.hook && (
                                        <div>
                                          <p className="text-[10px] uppercase tracking-wider font-semibold text-violet-500 mb-0.5">Hook</p>
                                          <p className="text-sm leading-relaxed">{script.hook}</p>
                                        </div>
                                      )}
                                      {script.body && (
                                        <div>
                                          <p className="text-[10px] uppercase tracking-wider font-semibold text-violet-500 mb-0.5">Body</p>
                                          <p className="text-sm leading-relaxed whitespace-pre-wrap">{script.body}</p>
                                        </div>
                                      )}
                                      {script.cta && (
                                        <div>
                                          <p className="text-[10px] uppercase tracking-wider font-semibold text-violet-500 mb-0.5">CTA</p>
                                          <p className="text-sm leading-relaxed">{script.cta}</p>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
      )}

      {isLocked && (scriptsLocked || hasPlans) && (
        <div className="space-y-6">
          {(() => {
            const allScripts = scripts || [];
            const totalScripts = allScripts.length;
            const castSize = selectedCharacters.length > 0 ? selectedCharacters.length : selectedAvatars.length;

            // Step 1 (script chain): narration → voice-over → beat map. A script
            // is fully "ready" only when all three exist; partial scripts stay
            // in-flight / "Continue". Full script readiness (beat map included)
            // gates the rest of the pipeline (storyboards onward) — narration
            // alone is NOT enough, since storyboards depend on the beat map.
            const parseFull = (s: any) => { try { return typeof s.fullScript === "string" ? JSON.parse(s.fullScript) : (s.fullScript || {}); } catch { return {}; } };
            const scriptHasNarration = (s: any) => {
              if (typeof s.body === "string" && s.body.trim().length > 0) return true;
              const f = parseFull(s);
              return Array.isArray(f?.scenes) && f.scenes.some((sc: any) => typeof sc?.narration === "string" && sc.narration.trim().length > 0);
            };
            const scriptHasBeats = (s: any) => { const f = parseFull(s); return Array.isArray(f?.beats) && f.beats.length > 0; };
            // The beat map runs after a successful voice-over; if it can't be
            // produced the chain persists this marker so the Script step shows
            // "failed" (instead of an incomplete-but-not-failed limbo that would
            // stall the "Generate All" auto-pilot).
            const scriptBeatsFailed = (s: any) => parseFull(s).beatsStatus === "failed";
            // The Script step is "ready" for a script once narration AND the
            // voice-over both exist. The beat map is now a SEPARATE step, so it no
            // longer gates the Script step.
            const scriptReady = allScripts.filter((s: any) => scriptHasNarration(s) && s.voiceOverStatus === "ready" && s.voiceOverUrl).length;
            const scriptGen = allScripts.filter((s: any) => s.voiceOverStatus === "generating").length;
            const scriptFail = allScripts.filter((s: any) => s.voiceOverStatus === "failed").length;
            const scriptBusy = isWritingScripts || scriptGen > 0;

            // Storyboards — up to three per script (one per 27s of ad
            // length). Count each slot independently; slots 2 & 3 only count
            // toward the total once in play (generating/ready/failed).
            const sbReady = allScripts.reduce((n: number, s: any) =>
              n + (s.storyboardStatus === "ready" && s.storyboardImageUrl ? 1 : 0)
                + (s.storyboardStatus2 === "ready" && s.storyboardImageUrl2 ? 1 : 0)
                + (s.storyboardStatus3 === "ready" && s.storyboardImageUrl3 ? 1 : 0), 0);
            const sbGen = allScripts.reduce((n: number, s: any) =>
              n + (s.storyboardStatus === "generating" ? 1 : 0)
                + (s.storyboardStatus2 === "generating" ? 1 : 0)
                + (s.storyboardStatus3 === "generating" ? 1 : 0), 0);
            const sbFail = allScripts.reduce((n: number, s: any) =>
              n + (s.storyboardStatus === "failed" ? 1 : 0)
                + (s.storyboardStatus2 === "failed" ? 1 : 0)
                + (s.storyboardStatus3 === "failed" ? 1 : 0), 0);
            // Total storyboard slots: slot 1 for every script, plus slots 2 & 3
            // for any script where they're active (so shorter scripts don't
            // inflate the denominator).
            const sbSlot2Active = allScripts.filter((s: any) =>
              s.storyboardStatus2 === "generating" || s.storyboardStatus2 === "ready" || s.storyboardStatus2 === "failed").length;
            const sbSlot3Active = allScripts.filter((s: any) =>
              s.storyboardStatus3 === "generating" || s.storyboardStatus3 === "ready" || s.storyboardStatus3 === "failed").length;
            const sbTotal = totalScripts + sbSlot2Active + sbSlot3Active;

            // Panels are sliced from storyboards automatically (server-side
            // after storyboards render, and on-demand by downstream steps), so
            // they are no longer surfaced as their own pipeline step. Extracted
            // panels remain viewable in each script's expanded view below.

            // Scene Prompts
            const promptReady = allScripts.filter((s: any) => {
              const ps = (s.scenePrompts as any[]) || [];
              return ps.length > 0 && ps.every((p: any) => p?.prompt);
            }).length;

            // Scene Videos — count true in-flight work (jobs with no
            // videoUrl AND no error) so refreshing mid-run still shows activity
            // and the action stays disabled while work is pending server-side.
            const vidReady = allScripts.filter((s: any) => {
              const vs = (s.sceneVideos as any[]) || [];
              return vs.length > 0 && vs.every((v: any) => v?.videoUrl);
            }).length;
            const vidInflight = allScripts.filter((s: any) => {
              const vs = (s.sceneVideos as any[]) || [];
              if (vs.length === 0) return false;
              const allReady = vs.every((v: any) => v?.videoUrl);
              if (allReady) return false;
              return vs.some((v: any) => !v?.videoUrl && !v?.error && v?.status !== "paused");
            }).length;
            // Scripts that have at least one scene paused mid-generation (no clip
            // yet, explicitly paused) — surfaces the Restart control.
            const vidPaused = allScripts.filter((s: any) => {
              const vs = (s.sceneVideos as any[]) || [];
              if (vs.length === 0) return false;
              if (vs.every((v: any) => v?.videoUrl)) return false;
              return vs.some((v: any) => !v?.videoUrl && v?.status === "paused");
            }).length;
            const vidFail = allScripts.filter((s: any) => {
              const vs = (s.sceneVideos as any[]) || [];
              if (vs.length === 0) return false;
              const allReady = vs.every((v: any) => v?.videoUrl);
              if (allReady) return false;
              const anyPending = vs.some((v: any) => !v?.videoUrl && !v?.error);
              if (anyPending) return false;
              return vs.some((v: any) => v?.error && !v?.videoUrl);
            }).length;

            // Final Render — eligible ONLY when every scene clip is ready
            // (all per-panel prompts have a rendered video), so the stitched cut
            // never drops a scene. Mirrors the backend `allSceneClipsReady` gate.
            const renderMap = rendersByScript || {};
            const renderEligible = allScripts.filter((s: any) => allClipsReady(s));
            const renderReady = renderEligible.filter((s: any) => renderMap[s.id]?.status === "complete").length;
            const renderGen = renderEligible.filter((s: any) => {
              const r = renderMap[s.id]; return r && (r.status === "queued" || r.status === "rendering");
            }).length;
            const renderFail = renderEligible.filter((s: any) => renderMap[s.id]?.status === "failed").length;

            // Captions step — only counts scripts whose final render is complete.
            // Total = completed renders; ready = captionedVideoStatus "ready";
            // generating = "generating"; failed = "failed".
            const capEligible = renderEligible.filter((s: any) => renderMap[s.id]?.status === "complete" && renderMap[s.id]?.outputUrl);
            const capReady = capEligible.filter((s: any) => (renderMap[s.id] as any)?.captionedVideoStatus === "ready").length;
            const capGen = capEligible.filter((s: any) => (renderMap[s.id] as any)?.captionedVideoStatus === "generating").length;
            const capFail = capEligible.filter((s: any) => (renderMap[s.id] as any)?.captionedVideoStatus === "failed").length;
            const capRenderIds = capEligible.map((s: any) => renderMap[s.id]?.id).filter(Boolean) as string[];

            // Gates
            const sbDone = sbReady > 0;
            const promptsDone = promptReady > 0;

            type Step = {
              key: string; n: number; label: string; icon: any;
              total: number; ready: number; generating: number; failed: number;
              busy: boolean; disabled: boolean; onClick: () => void;
              actionLabel: string; actionIcon: "spark" | "refresh" | "film";
              accent: string; barBg: string; gate?: string;
            };
            // Concept (step 1, concept-first / UGC parity). Done once plans
            // exist; a legacy campaign (locked scripts, no plans) is treated as
            // ready so the rest of the pipeline isn't blocked. The Script step
            // gates on this so concepts always come first.
            const conceptTotal = hasPlans ? plans!.length : (scriptsLocked ? (totalScripts || campaign?.videoCount || 1) : (campaign?.videoCount || 1));
            const conceptReady = hasPlans ? plans!.length : (scriptsLocked ? conceptTotal : 0);
            const conceptDone = conceptTotal > 0 && conceptReady === conceptTotal;
            const conceptBusy = isGeneratingConcepts || generateScriptPlansMutation.isPending;
            const conceptFail = conceptFailed && !hasPlans ? 1 : 0;

            // Expected script count = one per concept. Before scripts exist
            // (concepts generated, none written yet) totalScripts is 0, which
            // would mark the Script step "skip" and make the "Generate All"
            // auto-pilot stop before writing scripts — so fall back to the
            // concept/video count as the denominator until containers exist.
            const scriptTarget = totalScripts > 0 ? totalScripts : (hasPlans ? plans!.length : (campaign?.videoCount || 1));

            // The Script step is genuinely "complete" only when EVERY targeted
            // script is fully ready (narration + voice-over + beat map) with
            // nothing in flight or failed — mirrors the Script step's "done"
            // snapshot. Storyboards gate on this, NOT on a single ready script,
            // so they can't race ahead while a later script's beat map is still
            // pending (or has failed).
            const scriptStepComplete = scriptTarget > 0 && scriptReady === scriptTarget && !scriptBusy && scriptFail === 0;

            // Beat Map step (after Script): one beat map per script. Gated until
            // the Script step is fully complete (every script narrated + voiced),
            // so the denominator is the planned script count. Storyboards in turn
            // gate on this step finishing (they depend on the beat map).
            const beatTotal = scriptTarget;
            const beatReady = allScripts.filter((s: any) => scriptHasBeats(s)).length;
            const beatGen = allScripts.filter((s: any) => beatMapInProgress(s)).length;
            const beatFail = allScripts.filter((s: any) => scriptBeatsFailed(s)).length;
            const beatBusy = isGeneratingBeats || beatGen > 0;
            const beatStepComplete = scriptStepComplete && beatTotal > 0 && beatReady === beatTotal && !beatBusy && beatFail === 0;

            const steps: Step[] = [
              {
                key: "concept", n: 1, label: "Concept", icon: Lightbulb,
                total: conceptTotal, ready: conceptReady,
                generating: conceptBusy ? Math.max(0, conceptTotal - conceptReady) : 0,
                failed: conceptFail,
                busy: conceptBusy,
                disabled: conceptBusy,
                onClick: () => triggerConceptGeneration(),
                actionLabel: conceptReady === 0 ? "Generate" : "Regenerate",
                actionIcon: conceptReady > 0 ? "refresh" : "spark",
                accent: "text-amber-600", barBg: "bg-amber-500",
              },
              {
                key: "script", n: 2, label: "Script", icon: FileText,
                total: scriptTarget, ready: scriptReady,
                generating: scriptGen + (isWritingScripts ? Math.max(0, scriptTarget - scriptReady - scriptGen) : 0),
                failed: scriptFail,
                busy: scriptBusy,
                disabled: scriptBusy || !conceptDone,
                onClick: () => generateScriptsMutation.mutate(scriptReady === scriptTarget && scriptTarget > 0 && totalScripts > 0 ? { replace: true } : undefined),
                actionLabel: !conceptDone ? "Needs concept" : scriptReady === 0 ? "Generate" : scriptReady === scriptTarget ? "Regenerate" : "Continue",
                actionIcon: scriptReady > 0 && scriptReady === scriptTarget ? "refresh" : "spark",
                accent: "text-fuchsia-600", barBg: "bg-fuchsia-500",
                gate: !conceptDone ? "Needs concept" : undefined,
              },
              {
                key: "beatmap", n: 3, label: "Beat Map", icon: ListOrdered,
                total: beatTotal, ready: beatReady,
                generating: beatGen + (isGeneratingBeats ? Math.max(0, beatTotal - beatReady - beatGen - beatFail) : 0),
                failed: beatFail,
                busy: beatBusy,
                // Gated until the Script step is fully complete (beats need each
                // script's voice-over).
                disabled: !scriptStepComplete || beatBusy,
                onClick: () => generateBeatMapsMutation.mutate(beatReady === beatTotal && beatTotal > 0 ? { replace: true } : undefined),
                actionLabel: !scriptStepComplete ? "Needs script" : beatReady === 0 ? "Generate" : beatReady === beatTotal ? "Regenerate" : "Continue",
                actionIcon: beatReady > 0 && beatReady === beatTotal ? "refresh" : "spark",
                accent: "text-sky-600", barBg: "bg-sky-500",
                gate: !scriptStepComplete ? "Needs script" : undefined,
              },
              {
                key: "storyboard", n: 4, label: "Storyboards", icon: ImageIcon,
                total: sbTotal, ready: sbReady, generating: sbGen + (isGeneratingPrompts ? Math.max(0, sbTotal - sbReady - sbGen) : 0), failed: sbFail,
                busy: isGeneratingPrompts || sbGen > 0,
                // Gate on the Beat Map step completing (every script has a beat
                // map) — storyboards depend on the beat map, so they stay locked
                // until the whole Beat Map step is done.
                disabled: !beatStepComplete || isGeneratingPrompts || sbGen > 0 || isGeneratingVideos,
                onClick: generateAllPrompts,
                actionLabel: !beatStepComplete ? "Needs beat map" : sbReady === 0 ? "Generate" : sbReady === sbTotal ? "Regenerate" : "Continue",
                actionIcon: sbReady > 0 && sbReady === sbTotal ? "refresh" : "spark",
                accent: "text-violet-600", barBg: "bg-violet-500",
                gate: !beatStepComplete ? "Needs beat map" : undefined,
              },
              {
                key: "prompts", n: 5, label: "Scene Prompts", icon: Sparkles,
                total: totalScripts, ready: promptReady,
                generating: isGeneratingScenePrompts ? Math.max(0, totalScripts - promptReady) : 0, failed: 0,
                busy: isGeneratingScenePrompts,
                disabled: !sbDone || isGeneratingScenePrompts || isGeneratingPrompts || isGeneratingVideos,
                onClick: generateScenePromptsFromStoryboard,
                actionLabel: promptReady === 0 ? "Generate" : promptReady === totalScripts ? "Regenerate" : "Continue",
                actionIcon: promptReady > 0 && promptReady === totalScripts ? "refresh" : "spark",
                accent: "text-violet-600", barBg: "bg-violet-500",
                gate: !sbDone ? "Needs storyboards" : undefined,
              },
              {
                key: "videos", n: 6, label: "Scene Videos", icon: Video,
                total: totalScripts, ready: vidReady,
                generating: vidInflight + (isGeneratingVideos ? Math.max(0, totalScripts - vidReady - vidInflight - vidFail) : 0),
                failed: vidFail,
                busy: isGeneratingVideos || vidInflight > 0,
                disabled: !promptsDone || isGeneratingVideos || isGeneratingPrompts || vidInflight > 0,
                onClick: generateAllVideos,
                actionLabel: vidReady === 0 ? "Generate" : vidReady === totalScripts ? "Regenerate" : "Continue",
                actionIcon: vidReady > 0 && vidReady === totalScripts ? "refresh" : "spark",
                accent: "text-emerald-600", barBg: "bg-emerald-500",
                gate: !promptsDone ? "Needs prompts" : undefined,
              },
              {
                key: "final", n: 7, label: "Final Render", icon: Film,
                total: renderEligible.length, ready: renderReady, generating: renderGen, failed: renderFail,
                busy: renderGen > 0 || renderAllMutation.isPending,
                disabled: renderEligible.length === 0 || renderGen > 0 || renderAllMutation.isPending,
                onClick: () => renderAllMutation.mutate(),
                actionLabel: renderEligible.length === 0
                  ? "Needs all clips"
                  : renderReady === 0 ? "Render"
                  : renderReady === renderEligible.length ? "Re-render"
                  : `Render ${renderEligible.length - renderReady}`,
                actionIcon: renderReady > 0 && renderReady === renderEligible.length ? "refresh" : "film",
                accent: "text-violet-600", barBg: "bg-violet-500",
                gate: renderEligible.length === 0 ? "Needs every scene clip ready" : undefined,
              },
              {
                key: "captions", n: 8, label: "Captions", icon: Captions,
                total: capEligible.length, ready: capReady, generating: capGen, failed: capFail,
                busy: capGen > 0 || captionAllMutation.isPending,
                disabled: capEligible.length === 0 || capGen > 0 || captionAllMutation.isPending,
                onClick: () => captionAllMutation.mutate(capRenderIds),
                actionLabel: capEligible.length === 0
                  ? "Needs final render"
                  : capReady === 0 ? "Caption"
                  : capReady === capEligible.length ? "Re-caption"
                  : `Caption ${capEligible.length - capReady}`,
                actionIcon: capReady > 0 && capReady === capEligible.length ? "refresh" : "spark",
                accent: "text-fuchsia-600", barBg: "bg-fuchsia-500",
                gate: capEligible.length === 0 ? "Needs final render" : undefined,
              },
            ];

            const ActionIcon = ({ kind }: { kind: "spark" | "refresh" | "film" }) =>
              kind === "refresh" ? <RefreshCw className="h-3 w-3 mr-1" />
              : kind === "film" ? <Film className="h-3 w-3 mr-1" />
              : <Sparkles className="h-3 w-3 mr-1" />;

            // Snapshot the steps for the "Generate All" auto-pilot effect. A step
            // is "done" when every unit is ready with nothing in flight or failed.
            pipelineRef.current = steps.map((s) => ({
              key: s.key,
              done: s.total > 0 && s.ready === s.total && s.generating === 0 && s.failed === 0 && !s.busy,
              // "skip" = nothing to do for this step (e.g. Voice-Over when no
              // script has narration). The auto-pilot steps over these instead of
              // stalling on a step that can never reach "done".
              skip: s.total === 0,
              busy: s.busy,
              disabled: s.disabled,
              gated: !!s.gate,
              failed: s.failed > 0,
              onClick: s.onClick,
            }));
            // The whole pipeline is complete when every step is either done or
            // has nothing to do.
            const pipelineAllDone = pipelineRef.current.every((s) => s.done || s.skip);

            return (
              <div className="space-y-3">
              <div className="rounded-2xl border border-border/40 bg-card shadow-sm overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-border/30 bg-gradient-to-r from-emerald-500/[0.05] via-emerald-500/[0.02] to-transparent">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
                      <Check className="h-[18px] w-[18px] text-emerald-600" />
                    </div>
                    <div className="min-w-0">
                      <h2 className="text-base font-bold tracking-tight" data-testid="heading-avatar-cards">Production Pipeline</h2>
                      <p className="text-xs text-muted-foreground">{castSize} cast · {scriptsLocked ? `${totalScripts} script${totalScripts === 1 ? "" : "s"} locked` : `${conceptReady} concept${conceptReady === 1 ? "" : "s"} ready`}</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => {
                      if (autoRunPipeline) {
                        setAutoRunPipeline(false);
                        autoRunFiredRef.current = null;
                      } else {
                        autoRunFiredRef.current = null;
                        setAutoRunPipeline(true);
                      }
                    }}
                    disabled={!autoRunPipeline && pipelineAllDone}
                    variant={autoRunPipeline ? "outline" : "default"}
                    className={`h-8 rounded-full text-xs font-medium px-3 shrink-0 ${autoRunPipeline ? "" : "bg-violet-600 hover:bg-violet-700 text-white shadow-sm"}`}
                    data-testid="button-generate-all"
                    title={pipelineAllDone ? "All steps complete" : "Run every pipeline step in order"}
                  >
                    {autoRunPipeline
                      ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Running… (stop)</>
                      : <><Wand2 className="h-3.5 w-3.5 mr-1.5" /> Generate All</>}
                  </Button>
                </div>

                {/* 6-step grid — rows of three. Explicit dividers per breakpoint:
                    mobile (1col): top border on every row after the first;
                    sm (2col): top border on rows 2+ (i>=2), left border on right col (odd i);
                    lg (3col): top border on the second row (i>=3), left border on cols 2-3 (i%3 !== 0). */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                  {steps.map((s, i) => {
                    const pct = s.total === 0 ? 0 : Math.round((s.ready / s.total) * 100);
                    const allDone = s.total > 0 && s.ready === s.total && s.generating === 0 && s.failed === 0;
                    const borderClasses = [
                      i > 0 ? "border-t" : "",
                      i >= 2 ? "sm:border-t" : "sm:border-t-0",
                      i % 2 === 1 ? "sm:border-l" : "sm:border-l-0",
                      i >= 3 ? "lg:border-t" : "lg:border-t-0",
                      i % 3 === 0 ? "lg:border-l-0" : "lg:border-l",
                    ].filter(Boolean).join(" ");
                    return (
                      <div
                        key={s.key}
                        className={`p-5 flex flex-col border-border/30 ${borderClasses}`}
                        data-testid={`pipeline-step-${s.key}`}
                      >
                        {/* Title row */}
                        <div className="flex items-center gap-2.5 mb-3">
                          <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-[11px] font-bold shrink-0 ${
                            allDone ? "bg-emerald-500 text-white"
                            : s.busy ? "bg-amber-500 text-white"
                            : s.failed > 0 ? "bg-red-500 text-white"
                            : s.disabled && !s.busy ? "bg-muted/50 text-muted-foreground/70"
                            : "bg-violet-500/15 text-violet-600"
                          }`}>
                            {allDone ? <Check className="h-3.5 w-3.5" /> : s.n}
                          </div>
                          <p className="text-xs uppercase tracking-wider font-bold text-foreground/80 flex-1 min-w-0 truncate">{s.label}</p>
                          <s.icon className={`h-4 w-4 ${s.accent} opacity-70 shrink-0`} />
                        </div>

                        {/* Counts */}
                        <div className="flex items-baseline gap-1.5 mb-2">
                          <span className="text-3xl font-bold tabular-nums leading-none">{s.ready}</span>
                          <span className="text-sm text-muted-foreground">/ {s.total}</span>
                          <span className="text-xs text-muted-foreground/60 ml-auto tabular-nums">{pct}%</span>
                        </div>

                        {/* Progress bar */}
                        <div className="h-1.5 rounded-full bg-muted/40 overflow-hidden mb-2.5">
                          <div className={`h-full ${s.barBg} transition-all duration-500`} style={{ width: `${pct}%` }} />
                        </div>

                        {/* Status mini line */}
                        <div className="text-[10px] mb-2.5 min-h-[14px] flex items-center gap-1.5 flex-wrap">
                          {s.busy && (
                            <span className="inline-flex items-center gap-1 text-amber-600 font-semibold">
                              <Loader2 className="h-2.5 w-2.5 animate-spin" /> Processing
                            </span>
                          )}
                          {!s.busy && s.generating > 0 && (
                            <span className="inline-flex items-center gap-1 text-amber-600 font-semibold">
                              <Loader2 className="h-2.5 w-2.5 animate-spin" /> {s.generating} in flight
                            </span>
                          )}
                          {s.failed > 0 && (
                            <span className="inline-flex items-center gap-1 text-red-600 font-semibold">
                              <X className="h-2.5 w-2.5" /> {s.failed} failed
                            </span>
                          )}
                          {!s.busy && s.generating === 0 && s.failed === 0 && allDone && (
                            <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold">
                              <Check className="h-2.5 w-2.5" /> Complete
                            </span>
                          )}
                          {!s.busy && s.generating === 0 && s.failed === 0 && !allDone && s.gate && (
                            <span className="inline-flex items-center gap-1 text-muted-foreground/60">
                              <AlertTriangle className="h-2.5 w-2.5" /> {s.gate}
                            </span>
                          )}
                          {!s.busy && s.generating === 0 && s.failed === 0 && !allDone && !s.gate && s.ready === 0 && (
                            <span className="text-muted-foreground/50">Not started</span>
                          )}
                          {!s.busy && s.generating === 0 && s.failed === 0 && !allDone && !s.gate && s.ready > 0 && (
                            <span className="text-muted-foreground/60">{s.total - s.ready} pending</span>
                          )}
                        </div>

                        {/* Action — the Scene Prompts step swaps to a Cancel
                            button while generating so a stuck/long run can be
                            aborted and retried. */}
                        {s.key === "prompts" && s.busy ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="w-full h-8 text-xs rounded-full mt-auto border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700 dark:border-red-800 dark:hover:bg-red-950/40"
                            disabled={isCancellingScenePrompts}
                            onClick={cancelScenePrompts}
                            data-testid="button-cancel-scene-prompts"
                          >
                            {isCancellingScenePrompts ? (
                              <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Cancelling…</>
                            ) : (
                              <><X className="h-3 w-3 mr-1" /> Cancel</>
                            )}
                          </Button>
                        ) : (
                        <Button
                          size="sm"
                          variant={(!s.gate && !allDone && !s.disabled) ? "default" : "outline"}
                          className={`w-full h-8 text-xs rounded-full mt-auto ${(!s.gate && !allDone && !s.disabled) ? "bg-violet-600 hover:bg-violet-700 text-white shadow-sm" : ""}`}
                          disabled={s.disabled}
                          onClick={s.onClick}
                          data-testid={`button-step-${s.key}`}
                          title={s.gate || ""}
                        >
                          {s.busy ? (
                            <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Running…</>
                          ) : (
                            <><ActionIcon kind={s.actionIcon} /> {s.actionLabel}</>
                          )}
                        </Button>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Inline progress strips for active batch ops */}
                {isGeneratingPrompts && promptGenStatus.total > 0 && (() => {
                  const { total, sent, done, errors } = promptGenStatus;
                  const completed = done + errors;
                  const pct = Math.round((completed / total) * 100);
                  return (
                    <div className="px-5 py-3 border-t border-border/30 bg-violet-500/[0.03]">
                      <div className="flex items-center justify-between mb-1.5">
                        <p className="text-[11px] text-muted-foreground">
                          <Sparkles className="inline h-3 w-3 mr-1 text-violet-500" />
                          Storyboards: {sent} sent, {done} done{errors > 0 ? `, ${errors} errors` : ""} of {total} scenes
                        </p>
                        <p className="text-[11px] font-medium text-muted-foreground tabular-nums">{pct}%</p>
                      </div>
                      <div className="h-1.5 rounded-full bg-muted/30 overflow-hidden">
                        <div className="h-full rounded-full bg-violet-500 transition-all duration-500" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })()}
                {isGeneratingVideos && Object.keys(genProgress).length > 0 && (() => {
                  const total = Object.keys(genProgress).length;
                  const done = Object.values(genProgress).filter(v => v === "done" || v === "error").length;
                  const pct = Math.round((done / total) * 100);
                  return (
                    <div className="px-5 py-3 border-t border-border/30 bg-emerald-500/[0.03]">
                      <div className="flex items-center justify-between mb-1.5">
                        <p className="text-[11px] text-muted-foreground">
                          <Video className="inline h-3 w-3 mr-1 text-emerald-500" />
                          Scene videos: {done} of {total} scripts processed
                        </p>
                        <p className="text-[11px] font-medium text-muted-foreground tabular-nums">{pct}%</p>
                      </div>
                      <div className="h-1.5 rounded-full bg-muted/30 overflow-hidden">
                        <div className="h-full rounded-full bg-emerald-500 transition-all duration-500" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })()}
                {/* Pause / Restart control — shown while scene videos are
                    rendering server-side or sitting paused mid-run. */}
                {(vidInflight > 0 || vidPaused > 0) && (
                  <div className="px-5 py-3 border-t border-border/30 bg-emerald-500/[0.03] flex items-center justify-between gap-3">
                    <p className="text-[11px] text-muted-foreground flex items-center gap-1.5 min-w-0">
                      {vidInflight === 0 && vidPaused > 0 ? (
                        <><Pause className="h-3 w-3 text-amber-500 shrink-0" /> Scene video generation paused — {vidPaused} script{vidPaused === 1 ? "" : "s"} incomplete</>
                      ) : (
                        <><Loader2 className="h-3 w-3 animate-spin text-emerald-500 shrink-0" /> Rendering scene videos — {vidInflight} script{vidInflight === 1 ? "" : "s"} in flight</>
                      )}
                    </p>
                    {vidInflight === 0 && vidPaused > 0 ? (
                      <Button
                        size="sm"
                        className="h-7 rounded-full text-[11px] px-3 bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
                        onClick={restartSceneVideos}
                        disabled={isRestartingVideos}
                        data-testid="button-restart-scene-videos"
                      >
                        {isRestartingVideos
                          ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Restarting…</>
                          : <><RefreshCw className="h-3 w-3 mr-1" /> Restart</>}
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 rounded-full text-[11px] px-3 shrink-0"
                        onClick={pauseSceneVideos}
                        disabled={isPausingVideos}
                        data-testid="button-pause-scene-videos"
                      >
                        {isPausingVideos
                          ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Pausing…</>
                          : <><Pause className="h-3 w-3 mr-1" /> Pause</>}
                      </Button>
                    )}
                  </div>
                )}
              </div>
              </div>
            );
          })()}

          {!scriptsLocked && hasPlans && (
            <div className="space-y-3" data-testid="section-concept-review">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-bold flex items-center gap-2">
                  <Lightbulb className="h-4 w-4 text-amber-500" />
                  Concepts
                  <span className="text-xs text-muted-foreground font-normal">({plans!.length})</span>
                </h2>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-full"
                  onClick={() => triggerConceptGeneration()}
                  disabled={isGeneratingConcepts || generateScriptPlansMutation.isPending}
                  data-testid="button-regenerate-all-concepts"
                >
                  <RefreshCw className={`h-3.5 w-3.5 mr-1 ${isGeneratingConcepts || generateScriptPlansMutation.isPending ? "animate-spin" : ""}`} /> Regenerate All
                </Button>
              </div>

              <div className="space-y-2">
                {[...plans!].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)).map((plan, idx) => {
                  const regenning = regenerateScriptPlanMutation.isPending && regenerateScriptPlanMutation.variables === plan.id;
                  const editing = editingPlanId === plan.id;
                  const cj = (plan.conceptJson && typeof plan.conceptJson === "object" && !Array.isArray(plan.conceptJson))
                    ? (plan.conceptJson as Record<string, any>)
                    : null;
                  const conceptName = cj ? conceptText(cj.concept_name) : "";
                  // A stored "Concept N" placeholder title (saved before concept_name
                  // was picked up) should defer to the real concept_name.
                  const conceptDisplayName =
                    plan.title && !/^Concept \d+$/.test(plan.title.trim())
                      ? plan.title
                      : conceptName || plan.title || "";
                  return (
                    <Card key={plan.id} className="border-border/50" data-testid={`card-concept-${plan.id}`}>
                      <CardContent className="p-4">
                        {editing ? (
                          <div className="space-y-2">
                            <input
                              className="w-full text-sm font-semibold bg-transparent border border-border/60 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-500"
                              value={editingPlanTitle}
                              onChange={(e) => setEditingPlanTitle(e.target.value)}
                              placeholder="Concept title"
                              data-testid={`input-concept-title-${plan.id}`}
                            />
                            <Textarea
                              className="text-sm min-h-[80px]"
                              value={editingPlanDescription}
                              onChange={(e) => setEditingPlanDescription(e.target.value)}
                              placeholder="Concept description"
                              data-testid={`input-concept-description-${plan.id}`}
                            />
                            <div className="flex items-center gap-2 justify-end">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="rounded-full"
                                onClick={() => setEditingPlanId(null)}
                                data-testid={`button-cancel-concept-${plan.id}`}
                              >
                                <X className="h-3.5 w-3.5 mr-1" /> Cancel
                              </Button>
                              <Button
                                size="sm"
                                className="rounded-full"
                                onClick={() => updateScriptPlanMutation.mutate({ planId: plan.id, title: editingPlanTitle, conceptDescription: editingPlanDescription })}
                                disabled={updateScriptPlanMutation.isPending}
                                data-testid={`button-save-concept-${plan.id}`}
                              >
                                <Save className="h-3.5 w-3.5 mr-1" /> Save
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-1.5">
                            <div className="flex items-start justify-between gap-3">
                              <h3 className="text-sm font-semibold flex items-center gap-2" data-testid={`text-concept-title-${plan.id}`}>
                                <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-amber-500/10 text-amber-600 text-[11px] font-bold shrink-0">{idx + 1}</span>
                                {conceptDisplayName || `Concept ${idx + 1}`}
                              </h3>
                              <div className="flex items-center gap-1 shrink-0">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2 rounded-full"
                                  onClick={() => {
                                    setEditingPlanId(plan.id);
                                    setEditingPlanTitle(conceptDisplayName);
                                    setEditingPlanDescription(plan.conceptDescription || "");
                                  }}
                                  data-testid={`button-edit-concept-${plan.id}`}
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2 rounded-full"
                                  onClick={() => regenerateScriptPlanMutation.mutate(plan.id)}
                                  disabled={regenning}
                                  data-testid={`button-regenerate-concept-${plan.id}`}
                                >
                                  <RefreshCw className={`h-3.5 w-3.5 ${regenning ? "animate-spin" : ""}`} />
                                </Button>
                              </div>
                            </div>
                            <div data-testid={`text-concept-description-${plan.id}`}>
                              <ConceptDetails concept={plan.conceptJson} fallback={plan.conceptDescription} />
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              <div className="flex justify-end pt-1">
                <Button
                  onClick={() => generateScriptsMutation.mutate(undefined)}
                  disabled={generateScriptsMutation.isPending || isWritingScripts}
                  className="rounded-full bg-violet-600 hover:bg-violet-700 text-white"
                  data-testid="button-generate-scripts-from-concepts"
                >
                  {generateScriptsMutation.isPending || isWritingScripts ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4 mr-1" />
                  )}
                  Generate Scripts from Concepts
                </Button>
              </div>
            </div>
          )}

          {(() => {
            const finalScripts = (scripts || [])
              .map((script: any) => {
                const r = rendersByScript?.[script.id];
                if (r?.status !== "complete") return null;
                // Never show the uncaptioned stitch. Only play the final video
                // once captions are burned in; until then show a waiting state.
                const capStatus = (r as any)?.captionedVideoStatus as string | undefined;
                const capUrl = (r as any)?.captionedVideoUrl as string | undefined;
                if (capStatus === "ready" && capUrl) return { script, url: capUrl, captioning: false };
                // Captioning (fal veed) is a separate, flaky post-step. When it
                // FAILS the stitched render itself still succeeded, so deliver the
                // completed uncaptioned final instead of presenting a good video as
                // "failed" (mirrors the per-script view + VE Max fallback to outputUrl).
                if (capStatus === "failed" && (r as any)?.outputUrl) {
                  return { script, url: (r as any).outputUrl as string, captioning: false };
                }
                // Otherwise captions are still in flight — keep the slot visible as
                // "Adding captions…" rather than the raw cut.
                return { script, url: undefined, captioning: capStatus !== "failed", failed: capStatus === "failed" };
              })
              .filter(Boolean) as { script: any; url?: string; captioning: boolean; failed?: boolean }[];
            if (finalScripts.length === 0) return null;
            const readyCount = finalScripts.filter((f) => !!f.url).length;
            return (
              <div className="rounded-2xl border border-border/40 overflow-hidden bg-card shadow-sm" data-testid="section-final-results">
                <div className="flex items-center justify-between px-5 py-4 border-b border-border/30 bg-emerald-500/[0.03]">
                  <h2 className="text-base font-bold flex items-center gap-2" data-testid="heading-final-results">
                    <Film className="h-4 w-4 text-emerald-500" />
                    Final Videos
                  </h2>
                  <span className="text-[11px] font-medium text-muted-foreground tabular-nums" data-testid="text-final-count">
                    {readyCount} ready
                  </span>
                </div>
                <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {finalScripts.map(({ script, url, captioning, failed }) => {
                    const displayTitle =
                      (script.title && script.title !== "Untitled Script" ? script.title : null)
                      || "Final video";
                    return (
                      <div
                        key={script.id}
                        className="rounded-xl border border-border/30 bg-muted/[0.04] overflow-hidden"
                        data-testid={`card-final-${script.id}`}
                      >
                        {url ? (
                          <video
                            src={url}
                            controls
                            playsInline
                            className="w-full aspect-[9/16] object-contain bg-black"
                            data-testid={`video-final-${script.id}`}
                          />
                        ) : (
                          <div
                            className="w-full aspect-[9/16] bg-muted/30 flex flex-col items-center justify-center gap-2 text-center px-4"
                            data-testid={`status-final-captioning-${script.id}`}
                          >
                            {failed ? (
                              <>
                                <AlertTriangle className="h-5 w-5 text-red-500" />
                                <p className="text-[11px] font-medium text-red-600">Captioning failed</p>
                                <p className="text-[10px] text-muted-foreground">Retry from the script's render panel.</p>
                              </>
                            ) : (
                              <>
                                <Loader2 className="h-5 w-5 animate-spin text-fuchsia-500" />
                                <p className="text-[11px] font-medium text-muted-foreground">Adding captions…</p>
                              </>
                            )}
                          </div>
                        )}
                        <div className="flex items-center justify-between gap-2 px-3 py-2.5">
                          <p className="text-[12px] font-semibold truncate" data-testid={`text-final-title-${script.id}`}>
                            {displayTitle}
                          </p>
                          {url && (
                            <a
                              href={url}
                              download
                              onClick={(e) => e.stopPropagation()}
                              className="flex items-center gap-1 text-[10px] font-semibold text-emerald-700 dark:text-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/20 px-2 py-0.5 rounded-full transition-colors shrink-0"
                              data-testid={`link-final-mp4-${script.id}`}
                              title="Download final MP4"
                            >
                              <Download className="h-2.5 w-2.5" /> MP4
                            </a>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          <div className="space-y-5">
            {(() => {
              const castPool: any[] = selectedCharacters.length > 0 ? selectedCharacters : selectedAvatars;
              return castPool.map((avatar: any) => {
              const avatarScripts = scripts?.filter(s => s.avatarId === avatar.id) || [];
              if (avatarScripts.length === 0) return null;
              return (
                <div key={avatar.id} className="rounded-2xl border border-border/40 overflow-hidden bg-card shadow-sm" data-testid={`card-avatar-final-${avatar.id}`}>
                  <div className="flex flex-col sm:flex-row">
                    <div className="flex-1 p-4">
                      <div className="flex items-center justify-between mb-3 px-1">
                        <p className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground/60">Content</p>
                        <p className="text-[10px] text-muted-foreground/40">{avatarScripts.length} of {scripts?.length || 0}</p>
                      </div>
                      <div className="space-y-1.5">
                        {avatarScripts.map((script, idx) => {
                          const isExpanded = expandedScriptId === script.id;
                          let tone = "";
                          try {
                            const parsed = typeof script.fullScript === "string" ? JSON.parse(script.fullScript) : script.fullScript;
                            if (parsed?.overall_tone) tone = parsed.overall_tone;
                          } catch {}
                          const genStatus = genProgress[script.id];
                          const existingSceneVids = (script.sceneVideos as any[]) || [];
                          const existingScenePrompts = (script.scenePrompts as any[]) || [];
                          const hasVideos = existingSceneVids.some((sv: any) => sv?.videoUrl);
                          const hasPrompts = existingScenePrompts.some((sp: any) => sp?.prompt);
                          const finalRender = rendersByScript?.[script.id];
                          const finalVideoUrl = ((finalRender as any)?.captionedVideoStatus === "ready" && (finalRender as any)?.captionedVideoUrl)
                            ? (finalRender as any).captionedVideoUrl as string
                            : finalRender?.outputUrl as string | undefined;
                          const finalReady = finalRender?.status === "complete" && !!finalVideoUrl;
                          return (
                            <div
                              key={script.id}
                              className="rounded-xl border border-border/20 bg-muted/[0.04]"
                              data-testid={`card-avatar-script-${script.id}`}
                            >
                              {(() => {
                                const slots = [
                                  { idx: 1, status: (script as any).storyboardStatus, url: (script as any).storyboardImageUrl, err: (script as any).storyboardError },
                                  { idx: 2, status: (script as any).storyboardStatus2, url: (script as any).storyboardImageUrl2, err: (script as any).storyboardError2 },
                                  { idx: 3, status: (script as any).storyboardStatus3, url: (script as any).storyboardImageUrl3, err: (script as any).storyboardError3 },
                                ].filter((s) => s.status && s.status !== "idle");

                                const hasNarration = (() => {
                                  try {
                                    const p = typeof script.fullScript === "string" ? JSON.parse(script.fullScript) : (script.fullScript as any);
                                    return Array.isArray(p?.scenes) && p.scenes.some((sc: any) => typeof sc?.narration === "string" && sc.narration.trim().length > 0);
                                  } catch { return false; }
                                })();
                                const voStat = (script as any).voiceOverStatus;
                                const voUrl = (script as any).voiceOverUrl;
                                const voErr = (script as any).voiceOverError;
                                const voBusy = voStat === "generating" || voSubmitting.has(script.id) || isGeneratingVoiceOvers;

                                const r = rendersByScript?.[script.id];
                                const hasAnyVideo = existingSceneVids.some((v: any) => v?.videoUrl);
                                const sceneVidReady = existingSceneVids.filter((v: any) => v?.videoUrl).length;

                                let parsed: any = null;
                                let scenes: any[] = [];
                                try {
                                  parsed = typeof script.fullScript === "string" ? JSON.parse(script.fullScript) : script.fullScript;
                                  if (parsed?.scenes && Array.isArray(parsed.scenes)) scenes = parsed.scenes;
                                } catch {}
                                const isRange = (v: any) => /^\s*\d+\s*[–-]\s*\d+\s*$/.test(String(v ?? ""));
                                const hasVoiceover = typeof parsed?.voiceover_script === "string" && parsed.voiceover_script.trim().length > 0;
                                const isVoiceoverFormat = hasVoiceover || (scenes.length > 0 && scenes.every((s: any) => !s.scene_action && isRange(s.scene_role)));

                                return (
                                  <div onClick={(e) => e.stopPropagation()} className="p-3.5 space-y-2">
                                    {(slots.length > 0 || script.status === "locked") && (
                                      <AssetDrawer title="Storyboards" icon={Clapperboard} iconClass="text-violet-500" testId={`drawer-storyboards-${script.id}`}>
                                        {slots.length > 0 && (
                                        <div className="grid grid-cols-2 gap-2">
                                          {slots.map((slot) => (
                                            <div key={slot.idx}>
                                              <p className="text-[9px] uppercase tracking-wider font-semibold text-muted-foreground/60 mb-1">
                                                Storyboard {slot.idx} <span className="normal-case font-normal">· {`scenes ${(slot.idx - 1) * 9 + 1}–${slot.idx * 9}`}</span>
                                              </p>
                                              {slot.status === "generating" && (
                                                <div className="h-28 rounded-lg bg-muted/30 flex items-center justify-center">
                                                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                                                </div>
                                              )}
                                              {slot.status === "ready" && slot.url && (
                                                <Dialog>
                                                  <DialogTrigger asChild>
                                                    <button type="button" className="block w-full overflow-hidden rounded-lg border border-border/40 hover:border-violet-500/40 transition-colors" data-testid={`button-storyboard-${slot.idx}-${script.id}`}>
                                                      <img src={slot.url} alt={`Storyboard ${slot.idx}`} loading="lazy" className="w-full h-28 object-cover" data-testid={`img-storyboard-${slot.idx}-${script.id}`} />
                                                    </button>
                                                  </DialogTrigger>
                                                  <DialogContent className="max-w-[95vw] w-fit p-2">
                                                    <DialogHeader className="sr-only">
                                                      <DialogTitle>Storyboard {slot.idx}</DialogTitle>
                                                    </DialogHeader>
                                                    <img src={slot.url} alt={`Storyboard ${slot.idx}`} className="max-w-full max-h-[85vh] w-auto h-auto object-contain rounded-lg mx-auto" />
                                                  </DialogContent>
                                                </Dialog>
                                              )}
                                              {slot.status === "failed" && (
                                                <p className="text-xs text-red-600" data-testid={`text-storyboard-error-${slot.idx}-${script.id}`}>{slot.err || "Generation failed"}</p>
                                              )}
                                              {slot.status !== "generating" && (
                                                <button
                                                  type="button"
                                                  onClick={(e) => { e.stopPropagation(); regenerateStoryboardSlot(script.id, slot.idx); }}
                                                  disabled={regenSlots.has(`${script.id}:${slot.idx}`)}
                                                  className="mt-1 flex items-center gap-1 text-[10px] font-semibold text-violet-600 hover:text-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                                  data-testid={`button-regenerate-storyboard-${slot.idx}-${script.id}`}
                                                  title="Regenerate this storyboard"
                                                >
                                                  <RefreshCw className={`h-2.5 w-2.5 ${regenSlots.has(`${script.id}:${slot.idx}`) ? "animate-spin" : ""}`} /> Regenerate
                                                </button>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                        )}
                                        <StoryboardUpload script={script} campaignId={claymationCampaignId} />
                                        <StoryboardPanelGrid script={script} campaignId={claymationCampaignId} variant="claymation" />
                                      </AssetDrawer>
                                    )}

                                    {hasNarration && (
                                      <AssetDrawer
                                        title="Voiceover"
                                        icon={Mic}
                                        iconClass="text-fuchsia-500"
                                        status={
                                          voBusy ? <span className="text-[10px] font-semibold text-amber-600 flex items-center gap-1"><Loader2 className="h-2.5 w-2.5 animate-spin" /> Generating</span>
                                          : voStat === "ready" ? <span className="text-[10px] font-semibold text-emerald-600">Ready</span>
                                          : voStat === "failed" ? <span className="text-[10px] font-semibold text-red-600">Failed</span>
                                          : undefined
                                        }
                                        testId={`drawer-voiceover-${script.id}`}
                                      >
                                        <div data-testid={`panel-vo-${script.id}`}>
                                          {!voBusy && voStat === "ready" && voUrl && (
                                            <audio controls src={voUrl} className="w-full h-9 mb-2" data-testid={`audio-voiceover-${script.id}`} />
                                          )}
                                          {!voBusy && voStat === "failed" && (
                                            <p className="text-[11px] text-red-600 mb-2 line-clamp-2" data-testid={`text-voiceover-error-${script.id}`}>{voErr || "Generation failed"}</p>
                                          )}
                                          <Button
                                            size="sm"
                                            variant={!voBusy && voStat === "ready" ? "outline" : "default"}
                                            className={`w-full h-8 text-[11px] rounded-full ${!voBusy && voStat !== "ready" ? "bg-fuchsia-600 hover:bg-fuchsia-700 text-white" : ""}`}
                                            disabled={voBusy}
                                            onClick={() => generateVoiceOver(script.id)}
                                            data-testid={`button-generate-voiceover-${script.id}`}
                                          >
                                            {voBusy ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Generating…</>
                                              : voStat === "ready" ? <><RefreshCw className="h-3 w-3 mr-1" /> Regenerate</>
                                              : voStat === "failed" ? <><RefreshCw className="h-3 w-3 mr-1" /> Retry</>
                                              : <><Sparkles className="h-3 w-3 mr-1" /> Generate</>}
                                          </Button>
                                        </div>
                                      </AssetDrawer>
                                    )}

                                    {(((script.panelImages as any[]) || []).length > 0 || existingSceneVids.length > 0 || !!(script as any).storyboardImageUrl) && (
                                      <AssetDrawer
                                        title="Scenes & Clips"
                                        icon={Video}
                                        iconClass="text-violet-500"
                                        status={<span className="text-[10px] font-semibold text-muted-foreground/60">{sceneVidReady}/{Math.max(((script.panelImages as any[]) || []).length, existingSceneVids.length)}</span>}
                                        testId={`drawer-scene-videos-${script.id}`}
                                      >
                                        <ScenePanelReview script={script} campaignId={claymationCampaignId} variant="claymation" />
                                      </AssetDrawer>
                                    )}

                                  </div>
                                );
                              })()}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              );
            });
            })()}
          </div>
        </div>
      )}

      {lightboxAvatar && (
        <Dialog open onOpenChange={(open) => { if (!open) setLightboxAvatar(null); }}>
          <DialogContent className="max-w-sm p-2" data-testid="dialog-avatar-lightbox">
            <img
              src={optimizedImg(lightboxAvatar.imageUrl, 800, 85)}
              alt="Avatar full size"
              className="w-full rounded-xl"
            />
            <div className="flex justify-center gap-2 py-2">
              {lightboxAvatar.status !== "selected" && (
                <Button
                  size="sm"
                  className="rounded-full"
                  disabled={isFull}
                  onClick={() => {
                    if (isFull) return;
                    updateAvatarMutation.mutate({ id: lightboxAvatar.id, status: "selected" });
                    setLightboxAvatar(null);
                  }}
                  data-testid="button-lightbox-select"
                >
                  <Check className="h-3 w-3 mr-1" /> {isFull ? "Slots Full" : "Select"}
                </Button>
              )}
              {lightboxAvatar.status !== "rejected" && (
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-full"
                  onClick={() => {
                    updateAvatarMutation.mutate({ id: lightboxAvatar.id, status: "rejected" });
                    setLightboxAvatar(null);
                  }}
                  data-testid="button-lightbox-reject"
                >
                  <X className="h-3 w-3 mr-1" /> Reject
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                className="rounded-full text-destructive hover:bg-destructive/10"
                onClick={() => {
                  deleteAvatarMutation.mutate(lightboxAvatar.id);
                  setLightboxAvatar(null);
                }}
                data-testid="button-lightbox-delete"
              >
                <Trash2 className="h-3 w-3 mr-1" /> Delete
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

    </div>
  );
}
